<template>
    <div class="knowledge-graph-canvas">
        <!-- 标签容器 -->
        <div ref="tagsContainer" class="tags-container"></div>
        <!-- 分组容器 -->
        <div ref="groupsContainer" class="groups-container"></div>
        <div ref="graphContainer" class="graph-container"></div>

        <!-- 右键菜单 -->
        <div v-if="contextMenu.visible" class="context-menu"
            :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }" @click.stop>
            <div class="context-menu-item" @click="setAsSourceNode">
                <span class="menu-icon">🎯</span>
                设为源节点（待拓展）
            </div>
            <div class="context-menu-item" @click="setAsTargetNode">
                <span class="menu-icon">🎯</span>
                设为目标节点（待拓展）
            </div>
            <div class="context-menu-item" @click="addTagToNode">
                <span class="menu-icon">🏷️</span>
                添加标签（待拓展）
            </div>
            <div class="context-menu-item" @click="deleteNode">
                <span class="menu-icon">🗑️</span>
                删除节点（待拓展）
            </div>
            <div class="context-menu-item" @click="expandNode">
                <span class="menu-icon">🔍</span>
                拓展节点（待拓展）
            </div>
            <div class="context-menu-item" @click="editNode">
                <span class="menu-icon">✏️</span>
                编辑节点（待拓展）
            </div>

            <div class="context-menu-item" @click="toggleNodeLock">
                <span class="menu-icon">{{ selectedNodeForContextMenu?.locked ? '🔓' : '🔒' }}（待拓展）</span>
                {{ selectedNodeForContextMenu?.locked ? '解锁节点' : '锁定节点' }}
            </div>
            <div class="context-menu-item" @click="setAsTag">
                <span class="menu-icon">🏷️</span>
                设为标签
            </div>
            <div class="context-menu-item submenu-trigger"
                @mouseenter="() => { showGroupSubMenu = true; updateGroupMenu() }"
                @mouseleave="showGroupSubMenu = false">
                <span class="menu-icon">🎯</span>
                群组管理
                <div v-if="showGroupSubMenu" class="context-submenu"
                    @mouseenter="() => { showGroupSubMenu = true; updateGroupMenu() }"
                    @mouseleave="showGroupSubMenu = false">
                    <div v-for="group in store.groups" :key="group.id" class="context-menu-item"
                        :class="{ 'group-in': isNodeInGroup(selectedNodeForContextMenu?.value, group.id), 'group-out': !isNodeInGroup(selectedNodeForContextMenu?.value, group.id) }"
                        @click="() => toggleNodeGroup(group.id)">
                        <span class="menu-icon">{{ isNodeInGroup(selectedNodeForContextMenu?.value, group.id) ? '🔵' :
                            '⚪' }}</span>
                        {{ group.name }}
                    </div>
                </div>
            </div>
            <div class="context-menu-item submenu-trigger"
                @mouseenter="() => { showTagSubMenu = true; updateTagMenu() }" @mouseleave="showTagSubMenu = false">
                <span class="menu-icon">🏷️</span>
                标签管理
                <div v-if="showTagSubMenu" class="context-submenu"
                    @mouseenter="() => { showTagSubMenu = true; updateTagMenu() }" @mouseleave="showTagSubMenu = false">
                    <div v-for="tag in store.graphTypes" :key="tag.id" class="context-menu-item"
                        @click="() => handleTagSelection(tag)">
                        <span class="menu-icon">{{ isNodeInTag(tag.id) ? '🔵' : '⚪' }}</span>
                        {{ tag.name || tag.label }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import * as d3 from 'd3'
import { schemeCategory10 } from 'd3-scale-chromatic'
import { useKnowledgeGraphStore } from '../stores/knowledgeGraphStore.js'

export default {
    name: 'KnowledgeGraphCanvas',
    emits: ['node-click', 'edge-click', 'canvas-click', 'node-drag', 'node-edit', 'edge-edit', 'node-lock', 'node-highlight', 'add-relationship', 'delete-node', 'expand-node', 'delete-relationship'],
    setup(props, { emit }) {
        const store = useKnowledgeGraphStore()
        const graphContainer = ref(null)
        const tagsContainer = ref(null)
        const groupsContainer = ref(null)  // 添加分组容器引用
        let svg = null
        let simulation = null
        let linkGroup = null
        let nodeGroup = null
        let textGroup = null
        let dragGroup = null  // 添加这行
        let linkElements = null
        let nodeElements = null
        let textElements = null
        let width = 800
        let height = 600
        let selectedNode = null
        let selectedEdge = null
        let isDragging = false
        let isFullscreen = false
        let zoomBehavior = null
        let zoomContainer = null  // 添加zoomContainer变量
        // 添加布局类型变量，默认为力导向布局
        let layoutType = 'force'
        // 右键拖动光效相关变量
        let isRightClickDragging = false
        let dragPath = []
        let dragPathElement = null
        let dragCircleElement = null
        let lastRightClickTime = 0 // 添加这行用于双击检测
        console.log("selectedNode:", selectedNode)
        console.log("selectedEdge:", selectedEdge)
        console.log("isDragging:", isDragging)
        // 颜色比例尺
        const colorScale = schemeCategory10

        // 自定义颜色方案
        const customColorScheme = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
            '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
        ]

        // 根据graphId获取颜色的函数
        const getColorForGraphId = (graphId) => {
            // 如果graphId未定义或为空，返回默认颜色
            if (!graphId) return '#999';

            // 确保graphId是字符串类型
            const graphIdStr = String(graphId);

            // 使用graphId的哈希值来选择颜色
            const hash = Math.abs(graphIdStr.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0));
            return customColorScheme[hash % customColorScheme.length];
        }

        // 初始化图形
        const initGraph = () => {
            if (!graphContainer.value) {
                console.error('Graph container not found')
                return
            }

            console.log('初始化D3图形...')

            try {
                // 清除之前的SVG元素
                d3.select(graphContainer.value).select("svg").remove()

                // 清除可能残留的元素
                if (linkGroup) {
                    linkGroup.selectAll("*").remove()
                }
                if (nodeGroup) {
                    nodeGroup.selectAll("*").remove()
                }
                if (textGroup) {
                    textGroup.selectAll("*").remove()
                }
                if (dragGroup) {
                    dragGroup.selectAll("*").remove()
                }

                // 获取容器尺寸
                width = graphContainer.value.clientWidth || 800
                height = graphContainer.value.clientHeight || 600

                // 创建SVG元素
                svg = d3.select(graphContainer.value)
                    .append("svg")
                    .attr("width", width)
                    .attr("height", height)
                    .attr("class", "knowledge-graph-svg")

                // 创建缩放行为
                zoomBehavior = d3.zoom()
                    .scaleExtent([0.1, 10]) // 限制缩放范围
                    .on("zoom", handleZoom)

                // 添加缩放功能
                zoomContainer = svg.call(zoomBehavior)
                    .append("g")

                // 创建连线组
                linkGroup = zoomContainer.append("g").attr("class", "links")

                // 创建节点组
                nodeGroup = zoomContainer.append("g").attr("class", "nodes")

                // 创建文本组
                textGroup = zoomContainer.append("g").attr("class", "texts")

                // 创建拖动轨迹组
                dragGroup = zoomContainer.append("g").attr("class", "drag-group")

                // 初始化力导向图仿真
                initSimulation()

                // 加载初始数据
                loadGraphData()

            } catch (error) {
                console.error('D3图形初始化失败:', error)
            }
        }

        // 初始化标签显示
        const initTags = () => {
            console.log('开始初始化标签显示')
            console.log('当前store中的标签:', store.tags)

            if (!tagsContainer.value) {
                console.error('Tags container not found')
                return
            }

            // 清除之前的标签元素
            tagsContainer.value.innerHTML = ''

            // 如果没有标签，直接返回
            if (!store.tags || store.tags.length === 0) {
                console.log('没有标签需要显示')
                return
            }

            // 创建标签容器
            const tagsWrapper = document.createElement('div')
            tagsWrapper.className = 'tags-wrapper'

            // 添加标签
            store.tags.forEach((tag, index) => {
                console.log('处理标签:', tag)
                // 确保标签对象有name属性
                if (!tag || !tag.name) {
                    console.warn('标签对象不完整:', tag)
                    return
                }

                const tagElement = document.createElement('span')
                tagElement.className = 'tag-item'
                tagElement.textContent = tag.name
                tagElement.dataset.tagId = tag.id || index
                tagElement.dataset.tagName = tag.name

                // 设置标签颜色（存储在data属性中，不在style中）
                const colorIndex = index % customColorScheme.length
                tagElement.dataset.color = customColorScheme[colorIndex]
                // 立即设置背景色
                tagElement.style.backgroundColor = customColorScheme[colorIndex]
                tagElement.style.setProperty('background-color', customColorScheme[colorIndex], 'important')

                // 检查是否选中
                if (store.selectedTags.includes(tag.name)) {
                    tagElement.classList.add('selected')
                } else {
                    tagElement.classList.add('unselected')
                }

                // 如果当前处于全屏状态，添加全屏白色文字样式
                if (isFullscreen) {
                    tagElement.classList.add('fullscreen-white');
                }

                // 添加点击事件
                tagElement.addEventListener('click', () => {
                    toggleTagSelection(tag.name)
                })

                tagsWrapper.appendChild(tagElement)
            })

            tagsContainer.value.appendChild(tagsWrapper)
            console.log('标签显示初始化完成')
            // 调试：检查标签是否正确添加到DOM中
            console.log('标签容器内容:', tagsContainer.value.innerHTML)

            // 初始化完成后更新标签显示样式
            updateTagsDisplay()

            // 确保标签颜色正确设置
            setTimeout(() => {
                ensureTagColors()
                // 如果当前处于全屏状态，确保标签文字颜色为白色
                if (isFullscreen) {
                    const tagItems = tagsContainer.value.querySelectorAll('.tag-item');
                    tagItems.forEach(item => {
                        item.classList.add('fullscreen-white');
                    });
                }
            }, 50)

            // 初始化分组显示
            initGroups()
        }

        // 初始化分组显示
        const initGroups = () => {
            console.log('开始初始化分组显示')
            console.log('当前store中的分组:', store.groups)

            if (!groupsContainer.value) {
                console.error('Groups container not found')
                return
            }

            // 清除之前的分组元素
            groupsContainer.value.innerHTML = ''

            // 创建分组容器
            const groupsWrapper = document.createElement('div')
            groupsWrapper.className = 'groups-wrapper'

            // 添加分组
            store.groups.forEach((group, index) => {
                console.log('处理分组:', group)
                // 确保分组对象有name属性
                if (!group || !group.name) {
                    console.warn('分组对象不完整:', group)
                    return
                }

                const groupElement = document.createElement('span')
                groupElement.className = 'group-item'
                groupElement.textContent = group.name
                groupElement.dataset.groupId = group.id || index
                groupElement.dataset.groupName = group.name

                // 设置分组颜色（存储在data属性中，不在style中）
                const colorIndex = index % customColorScheme.length
                groupElement.dataset.color = group.color || customColorScheme[colorIndex]
                // 立即设置背景色
                groupElement.style.backgroundColor = group.color || customColorScheme[colorIndex]
                groupElement.style.setProperty('background-color', group.color || customColorScheme[colorIndex], 'important')

                // 检查是否选中
                if (store.filters.groups.includes(group.id)) {
                    groupElement.classList.add('selected')
                } else {
                    groupElement.classList.add('unselected')
                }

                // 如果当前处于全屏状态，添加全屏白色文字样式
                if (isFullscreen) {
                    groupElement.classList.add('fullscreen-white');
                }

                // 添加点击事件
                groupElement.addEventListener('click', () => {
                    toggleGroupSelection(group.id)
                })

                groupsWrapper.appendChild(groupElement)
            })

            // 添加一个 "+" 按钮用于添加新分组
            const addGroupButton = document.createElement('span')
            addGroupButton.className = 'add-group-button'
            addGroupButton.textContent = '+'
            addGroupButton.title = '添加新分组'
            addGroupButton.addEventListener('click', handleAddGroup)
            groupsWrapper.appendChild(addGroupButton)

            groupsContainer.value.appendChild(groupsWrapper)
            console.log('分组显示初始化完成')
            // 调试：检查分组是否正确添加到DOM中
            console.log('分组容器内容:', groupsContainer.value.innerHTML)

            // 初始化完成后更新分组显示样式
            updateGroupsDisplay()

            // 确保分组颜色正确设置
            setTimeout(() => {
                ensureGroupColors()
                // 如果当前处于全屏状态，确保分组文字颜色为白色
                if (isFullscreen) {
                    const groupItems = groupsContainer.value.querySelectorAll('.group-item');
                    groupItems.forEach(item => {
                        item.classList.add('fullscreen-white');
                    });
                }
            }, 50)
        }

        // 切换分组选中状态
        const toggleGroupSelection = (groupId) => {
            console.log('切换分组选中状态:', groupId)
            const currentSelectedGroups = [...store.filters.groups]
            const groupIndex = currentSelectedGroups.indexOf(groupId)

            if (groupIndex > -1) {
                // 如果已选中，则取消选中
                currentSelectedGroups.splice(groupIndex, 1)
            } else {
                // 如果未选中，则添加到选中列表
                currentSelectedGroups.push(groupId)
            }
            console.log("currentSelectedGroups:", currentSelectedGroups)
            // 更新store中的选中分组
            store.setFilters({ groups: currentSelectedGroups })

            // 更新分组显示
            updateGroupsDisplay()

            // 刷新图形（可能需要根据选中的分组过滤节点）
            refreshGraph()
        }

        // 更新分组显示
        const updateGroupsDisplay = () => {
            if (!groupsContainer.value) return
            console.log('更新分组显示')
            const groupElements = groupsContainer.value.querySelectorAll('.group-item')
            console.log('找到的分组元素数量:', groupElements.length)
            groupElements.forEach((groupElement, index) => {
                console.log(`分组${index}:`, groupElement)
                console.log(`分组${index}的类名:`, groupElement.className)
                console.log(`分组${index}的样式:`, groupElement.style.cssText)

                const groupId = groupElement.dataset.groupId
                const color = groupElement.dataset.color || '#4ECDC4' // 默认颜色改为自定义颜色方案中的一个

                // 设置背景色
                groupElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                groupElement.style.setProperty('background-color', color, 'important')

                console.log(`分组${index}设置背景色:`, color)

                if (store.filters.groups.includes(groupId)) {
                    groupElement.classList.remove('unselected')
                    groupElement.classList.add('selected')
                    console.log(`分组${index}设置为选中状态`)
                } else {
                    groupElement.classList.remove('selected')
                    groupElement.classList.add('unselected')
                    console.log(`分组${index}设置为未选中状态`)
                }

                // 如果当前处于全屏状态，确保分组文字颜色为白色
                if (isFullscreen) {
                    groupElement.classList.add('fullscreen-white');
                } else {
                    groupElement.classList.remove('fullscreen-white');
                }

                console.log(`分组${index}更新后的类名:`, groupElement.className)
                console.log(`分组${index}更新后的样式:`, groupElement.style.cssText)
            })
        }

        // 确保分组颜色正确设置
        const ensureGroupColors = () => {
            if (!groupsContainer.value) return
            const groupElements = groupsContainer.value.querySelectorAll('.group-item')
            groupElements.forEach(groupElement => {
                const color = groupElement.dataset.color || '#4ECDC4'
                groupElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                groupElement.style.setProperty('background-color', color, 'important')
                // 强制重绘
                groupElement.offsetHeight;
            })
        }

        // 强制刷新分组样式
        const forceRefreshGroups = () => {
            console.log('强制刷新分组样式')
            if (!groupsContainer.value) return

            const groupElements = groupsContainer.value.querySelectorAll('.group-item')
            groupElements.forEach((groupElement, index) => {
                console.log(`强制刷新分组${index}:`, groupElement)
                console.log(`分组${index}的类名:`, groupElement.className)

                // 重新应用类名
                const classes = groupElement.className.split(' ')
                groupElement.className = ''
                // 强制重排
                groupElement.offsetHeight
                // 重新应用类名
                groupElement.className = classes.join(' ')

                // 重新设置背景色
                const color = groupElement.dataset.color || '#4ECDC4'
                groupElement.style.backgroundColor = color
                groupElement.style.setProperty('background-color', color, 'important')

                // 如果当前处于全屏状态，确保分组文字颜色为白色
                if (isFullscreen) {
                    groupElement.classList.add('fullscreen-white');
                } else {
                    groupElement.classList.remove('fullscreen-white');
                }

                console.log(`分组${index}强制刷新后类名:`, groupElement.className)
            })
        }

        // 测试分组样式
        const testGroupStyles = () => {
            console.log('测试分组样式')
            if (!groupsContainer.value) return

            const groupElements = groupsContainer.value.querySelectorAll('.group-item')
            groupElements.forEach((groupElement, index) => {
                console.log(`测试分组${index}:`, groupElement)
                console.log(`分组${index}的类名:`, groupElement.className)
                console.log(`分组${index}的样式:`, groupElement.style.cssText)

                // 检查计算后的样式
                const computedStyle = window.getComputedStyle(groupElement)
                console.log(`分组${index}的计算样式:`, {
                    backgroundColor: computedStyle.backgroundColor,
                    color: computedStyle.color,
                    borderColor: computedStyle.borderColor,
                    opacity: computedStyle.opacity,
                    transform: computedStyle.transform
                })

                // 如果当前处于全屏状态，确保分组文字颜色为白色
                if (isFullscreen) {
                    groupElement.classList.add('fullscreen-white');
                } else {
                    groupElement.classList.remove('fullscreen-white');
                }
            })
        }

        // 切换标签选中状态
        const toggleTagSelection = (tagName) => {
            console.log('切换标签选中状态:', tagName)
            const currentSelectedTags = [...store.selectedTags]
            const tagIndex = currentSelectedTags.indexOf(tagName)

            if (tagIndex > -1) {
                // 如果已选中，则取消选中
                currentSelectedTags.splice(tagIndex, 1)
            } else {
                // 如果未选中，则添加到选中列表
                currentSelectedTags.push(tagName)
            }
            console.log("currentSelectedTags:", currentSelectedTags)
            // 更新store中的选中标签
            store.setSelectedTags(currentSelectedTags)

            // 更新标签显示
            updateTagsDisplay()

            // 刷新图形（可能需要根据选中的标签过滤节点）
            refreshGraph()
        }

        // 更新标签显示
        const updateTagsDisplay = () => {
            if (!tagsContainer.value) return
            console.log('更新标签显示')
            const tagElements = tagsContainer.value.querySelectorAll('.tag-item')
            console.log('找到的标签元素数量:', tagElements.length)
            tagElements.forEach((tagElement, index) => {
                console.log(`标签${index}:`, tagElement)
                console.log(`标签${index}的类名:`, tagElement.className)
                console.log(`标签${index}的样式:`, tagElement.style.cssText)

                const tagName = tagElement.dataset.tagName
                const color = tagElement.dataset.color || '#4ECDC4' // 默认颜色改为自定义颜色方案中的一个

                // 设置背景色
                tagElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                tagElement.style.setProperty('background-color', color, 'important')

                console.log(`标签${index}设置背景色:`, color)

                if (store.selectedTags.includes(tagName)) {
                    tagElement.classList.remove('unselected')
                    tagElement.classList.add('selected')
                    console.log(`标签${index}设置为选中状态`)
                } else {
                    tagElement.classList.remove('selected')
                    tagElement.classList.add('unselected')
                    console.log(`标签${index}设置为未选中状态`)
                }

                // 如果当前处于全屏状态，确保标签文字颜色为白色
                if (isFullscreen) {
                    tagElement.classList.add('fullscreen-white');
                } else {
                    tagElement.classList.remove('fullscreen-white');
                }

                console.log(`标签${index}更新后的类名:`, tagElement.className)
                console.log(`标签${index}更新后的样式:`, tagElement.style.cssText)
            })
        }

        // 确保标签颜色正确设置
        const ensureTagColors = () => {
            if (!tagsContainer.value) return
            const tagElements = tagsContainer.value.querySelectorAll('.tag-item')
            tagElements.forEach(tagElement => {
                const color = tagElement.dataset.color || '#4ECDC4'
                tagElement.style.backgroundColor = color
                // 确保背景色不会被覆盖
                tagElement.style.setProperty('background-color', color, 'important')
                // 强制重绘
                tagElement.offsetHeight;
            })
        }

        // 强制刷新标签样式
        const forceRefreshTags = () => {
            console.log('强制刷新标签样式')
            if (!tagsContainer.value) return

            const tagElements = tagsContainer.value.querySelectorAll('.tag-item')
            tagElements.forEach((tagElement, index) => {
                console.log(`强制刷新标签${index}:`, tagElement)
                console.log(`标签${index}的类名:`, tagElement.className)

                // 重新应用类名
                const classes = tagElement.className.split(' ')
                tagElement.className = ''
                // 强制重排
                tagElement.offsetHeight
                // 重新应用类名
                tagElement.className = classes.join(' ')

                // 重新设置背景色
                const color = tagElement.dataset.color || '#4ECDC4'
                tagElement.style.backgroundColor = color
                tagElement.style.setProperty('background-color', color, 'important')

                // 如果当前处于全屏状态，确保标签文字颜色为白色
                if (isFullscreen) {
                    tagElement.classList.add('fullscreen-white');
                } else {
                    tagElement.classList.remove('fullscreen-white');
                }

                console.log(`标签${index}强制刷新后类名:`, tagElement.className)
            })
        }

        // 测试标签样式
        const testTagStyles = () => {
            console.log('测试标签样式')
            if (!tagsContainer.value) return

            const tagElements = tagsContainer.value.querySelectorAll('.tag-item')
            tagElements.forEach((tagElement, index) => {
                console.log(`测试标签${index}:`, tagElement)
                console.log(`标签${index}的类名:`, tagElement.className)
                console.log(`标签${index}的样式:`, tagElement.style.cssText)

                // 检查计算后的样式
                const computedStyle = window.getComputedStyle(tagElement)
                console.log(`标签${index}的计算样式:`, {
                    backgroundColor: computedStyle.backgroundColor,
                    color: computedStyle.color,
                    borderColor: computedStyle.borderColor,
                    opacity: computedStyle.opacity,
                    transform: computedStyle.transform
                })

                // 如果当前处于全屏状态，确保标签文字颜色为白色
                if (isFullscreen) {
                    tagElement.classList.add('fullscreen-white');
                } else {
                    tagElement.classList.remove('fullscreen-white');
                }
            })
        }

        // 初始化力导向图仿真
        const initSimulation = () => {
            simulation = d3.forceSimulation()
                .force("link", d3.forceLink().id(d => d.id).distance(100))
                .force("charge", d3.forceManyBody().strength(-300))
                .force("center", d3.forceCenter(width / 2, height / 2))
                .force("collision", d3.forceCollide().radius(30))

            simulation.on("tick", ticked)
        }

        // 力导向图tick事件
        const ticked = () => {
            // 只有在力导向布局模式下才更新节点位置
            if (layoutType === 'force') {//力导向布局模式
                // 更新节点组位置
                if (nodeElements) {
                    nodeElements
                        .attr("transform", d => `translate(${d.x}, ${d.y})`)
                }


                // 更新文本位置
                if (textElements) {
                    textElements
                        .attr("x", d => d.x)
                        .attr("y", d => d.y + 5)
                }

                // 更新连线标签位置
                //zoomContainer是缩放管理器，其中包含了nodes,edges,texts等元素
                if (zoomContainer) {
                    const linkLabelElements = zoomContainer.select(".link-labels").selectAll("text")

                    if (linkLabelElements) {
                        linkLabelElements
                            .attr("x", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    // 根据线条样式调整标签位置
                                    switch (edgeStyleSettings.style) {
                                        case 'curve':
                                        case 'bezier':
                                            // 对于曲线，标签位置稍微偏移
                                            return (sourceNode.x + targetNode.x) / 2;
                                        case 'orthogonal':
                                            // 对于正交线，标签放在中间点
                                            return (sourceNode.x + targetNode.x) / 2;
                                        default:
                                            // 直线，标签放在中间
                                            return (sourceNode.x + targetNode.x) / 2;
                                    }
                                }
                                return 0
                            })
                            .attr("y", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    // 根据线条样式调整标签位置
                                    switch (edgeStyleSettings.style) {
                                        case 'curve':
                                            // 贝塞尔曲线，标签位置稍微向上偏移
                                            return (sourceNode.y + targetNode.y) / 2 - 15;
                                        case 'bezier':
                                            // 三次贝塞尔曲线，标签位置稍微向上偏移
                                            return (sourceNode.y + targetNode.y) / 2 - 20;
                                        case 'orthogonal':
                                            // 正交线，标签放在中间点
                                            return (sourceNode.y + targetNode.y) / 2;
                                        default:
                                            // 直线，标签放在中间
                                            return (sourceNode.y + targetNode.y) / 2;
                                    }
                                }
                                return 0
                            })

                        // 移除旧连线标签
                        linkLabelElements.exit().remove()

                        // 清除所有现有节点
                        // nodeGroup.selectAll("g").remove();

                    }
                }
            } else if (layoutType === 'tree') { //树形布局模式
                // 树形布局不需要在每次tick时更新，节点位置是固定的
                // 更新节点组位置
                if (nodeElements) {
                    nodeElements
                        .attr("transform", d => `translate(${d.x}, ${d.y})`)
                }

                // 更新文本位置
                if (textElements) {
                    textElements
                        .attr("x", d => d.x)
                        .attr("y", d => d.y + 5)
                }

                // 更新连线标签位置
                if (zoomContainer) {
                    const linkLabelElements = zoomContainer.select(".link-labels").selectAll("text")
                    if (linkLabelElements) {
                        linkLabelElements
                            .attr("x", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    return (sourceNode.x + targetNode.x) / 2
                                }
                                return 0;
                            })
                            .attr("y", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    return (sourceNode.y + targetNode.y) / 2
                                }
                                return 0;
                            })
                    }
                }
            } else if (layoutType === 'none') { //无布局模式
                // 在无布局模式下，确保节点位置不被更新
                // 更新节点组位置（使用固定位置）
                if (nodeElements) {
                    nodeElements
                        .attr("transform", d => `translate(${d.x}, ${d.y})`)
                }

                // 更新文本位置（使用固定位置）
                if (textElements) {
                    textElements
                        .attr("x", d => d.x)
                        .attr("y", d => d.y + 5)
                }

                // 更新连线标签位置（使用固定位置）
                //zoomContainer是缩放管理器，其中包含了nodes,edges,texts等元素
                if (zoomContainer) {
                    const linkLabelElements = zoomContainer.select(".link-labels").selectAll("text")
                    //如果存在连线标签元素
                    if (linkLabelElements) {//将x,y坐标设置为source和target中间
                        linkLabelElements
                            .attr("x", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    const sourceX = sourceNode.x
                                    // const sourceY = sourceNode.fy !== undefined ? sourceNode.fy : sourceNode.y
                                    const targetX = targetNode.x
                                    // const targetY = targetNode.fy !== undefined ? targetNode.fy : targetNode.y
                                    return (sourceX + targetX) / 2
                                }
                                return 0;
                            })
                            .attr("y", d => {
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                                if (sourceNode && targetNode) {
                                    const sourceY = sourceNode.y
                                    const targetY = targetNode.y
                                    return (sourceY + targetY) / 2
                                }
                                return 0;
                            })
                    }
                }
            }

            // 更新连线位置（始终更新，因为连线位置需要根据节点位置调整）
            if (linkElements) {
                linkElements
                    .attr("d", d => {
                        // 获取源节点和目标节点
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source);
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target);

                        // 创建路径数据
                        return createPathData(sourceNode, targetNode, edgeStyleSettings.style);
                    })
            }
        }

        // 缩放处理
        const handleZoom = (event) => {
            // 获取缩放容器并应用变换
            d3.select(graphContainer.value).select("svg").select("g")
                .attr("transform", event.transform);
        }

        // 添加树形布局算法
        const applyTreeLayout = () => {
            const nodes = store.graphData.nodes
            const edges = store.graphData.edges

            if (nodes.length === 0) return

            // 获取根节点
            let rootNodes = []

            // 获取所有作为source的节点（有出边的节点）
            const sourceNodeIds = new Set(edges.map(edge =>
                typeof edge.source === 'object' ? edge.source.id : edge.source
            ))

            // 根节点是没有出边的节点（即不在sourceNodeIds中的节点）
            //获取不是source的节点ID
            const actualRootNodes = nodes.filter(node => !sourceNodeIds.has(node.id))
            //如果没找到，就将第一个nodes节点作为根节点
            if (actualRootNodes.length === 0 && nodes.length > 0) {
                // 如果没有根节点（可能形成环），则选择第一个节点作为根节点
                rootNodes.push(nodes[0])
            } else {
                rootNodes = actualRootNodes
            }

            // 构建邻接表（子节点映射）
            // 根据需求，target节点作为父节点，source节点作为子节点
            const childrenMap = new Map()
            edges.forEach(edge => {
                const sourceId = typeof edge.source === 'object' ? edge.source.id : edge.source
                const targetId = typeof edge.target === 'object' ? edge.target.id : edge.target

                if (!childrenMap.has(targetId)) {
                    childrenMap.set(targetId, [])
                }
                childrenMap.get(targetId).push(sourceId)
            })
            console.log("childrenMap:", childrenMap)



            // 获取当前设置的树形布局方向
            const treeDirection = store.layoutConfig?.treeDirection || 'left-to-right'

            // 设置布局参数
            const horizontalSpacing = 150
            const verticalSpacing = 100

            // 计算每个节点的层级和位置
            const nodePositions = new Map()
            const nodeLevels = new Map()

            // 递归计算节点位置
            const calculatePositions = (nodeId, level, x, y, visited = new Set()) => {
                console.log("calculatePositions:", nodeId, level, x, y)
                if (visited.has(nodeId)) {
                    console.log("避免循环引用，跳过节点:", nodeId)
                    return // 避免循环引用
                }
                visited.add(nodeId)

                nodeLevels.set(nodeId, level)

                // 根据方向设置坐标
                let posX, posY
                switch (treeDirection) {
                    case 'top-to-bottom':
                        posX = x
                        posY = y + level * verticalSpacing
                        break
                    case 'right-to-left':
                        posX = x - level * horizontalSpacing
                        posY = y
                        break
                    case 'bottom-to-top':
                        posX = x
                        posY = y - level * verticalSpacing
                        break
                    case 'left-to-right':
                    default:
                        posX = x + level * horizontalSpacing
                        posY = y
                        break
                }
                //设置节点位置
                nodePositions.set(nodeId, { x: posX, y: posY })
                console.log("设置节点位置:", nodeId, posX, posY)

                // 获取子节点
                const children = childrenMap.get(nodeId) || []
                console.log("节点", nodeId, "的子节点:", children)

                // 如果没有子节点，直接返回
                if (children.length === 0) {
                    console.log("节点", nodeId, "没有子节点")
                }

                // 递归处理子节点
                children.forEach((childId, index) => {
                    console.log("处理子节点:", childId, "索引:", index)
                    // 计算子节点的坐标（在不同方向上分散）
                    let childX = posX, childY = posY
                    switch (treeDirection) {
                        case 'top-to-bottom':
                            // 垂直布局时，子节点在x方向分散
                            childX = posX + (index - (children.length - 1) / 2) * horizontalSpacing
                            childY = posY + verticalSpacing
                            break
                        case 'right-to-left':
                            // 从右到左布局时，子节点在y方向分散
                            childX = posX - horizontalSpacing
                            childY = posY + (index - (children.length - 1) / 2) * verticalSpacing
                            break
                        case 'bottom-to-top':
                            // 从下到上布局时，子节点在x方向分散
                            childX = posX + (index - (children.length - 1) / 2) * horizontalSpacing
                            childY = posY - verticalSpacing
                            break
                        case 'left-to-right':
                        default:
                            // 从左到右布局时，子节点在y方向分散
                            childX = posX + horizontalSpacing
                            childY = posY + (index - (children.length - 1) / 2) * verticalSpacing
                            break
                    }

                    console.log("子节点", childId, "计算位置:", childX, childY)
                    calculatePositions(childId, level + 1, childX, childY, new Set(visited))
                })
            }

            // 从根节点开始计算位置
            rootNodes.forEach((rootNode, index) => {
                const startX = 100
                const startY = 100 + index * 200
                calculatePositions(rootNode.id, 0, startX, startY)
            })

            // 更新节点位置
            nodes.forEach(node => {
                const position = nodePositions.get(node.id)
                if (position) {
                    node.x = position.x
                    node.y = position.y
                }
            })
        }

        // 存储边样式设置
        let edgeStyleSettings = {
            style: 'straight',
            colorScheme: 'default',
            width: 2,
            showArrow: true,
            arrowStyle: 'default',
            enableGlow: false,
            enableAnimation: false
        }

        // 设置边样式
        const setEdgeStyle = (settings) => {
            edgeStyleSettings = { ...edgeStyleSettings, ...settings }
            console.log('设置边样式:', edgeStyleSettings)

            // 应用新的样式设置到现有连线
            if (linkElements) {
                applyEdgeStyle(linkElements)
            }

            // 更新连线标签位置
            if (zoomContainer) {
                const linkLabelElements = zoomContainer.select(".link-labels").selectAll("text")
                linkLabelElements
                    .attr("x", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                case 'bezier':
                                    // 对于曲线，标签位置稍微偏移
                                    return (sourceNode.x + targetNode.x) / 2
                                case 'orthogonal':
                                    // 对于正交线，标签放在中间点
                                    return (sourceNode.x + targetNode.x) / 2
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.x + targetNode.x) / 2
                            }
                        }
                        return 0
                    })
                    .attr("y", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                    // 贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 15
                                case 'bezier':
                                    // 三次贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 20
                                case 'orthogonal':
                                    // 正交线，标签放在中间点
                                    return (sourceNode.y + targetNode.y) / 2
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.y + targetNode.y) / 2
                            }
                        }
                        return 0
                    })
            }
        }

        // 应用边样式到元素
        const applyEdgeStyle = (selection) => {
            if (!selection) return

            // 应用线宽
            selection.attr("stroke-width", d => {
                // 检查是否为选中的边
                if (selectedEdge && d.id === selectedEdge.id) {
                    return edgeStyleSettings.width + 1 // 保持选中边的高亮宽度
                }
                return d.style?.lineWidth || edgeStyleSettings.width
            })

            // 应用虚线样式
            selection.attr("stroke-dasharray", d => {
                console.log("d is :", d)
                if (edgeStyleSettings.style === 'dashed') {
                    return "5,5"
                } else {
                    return null
                }
            })

                // 应用发光效果
                .attr("filter", d => {
                    console.log("d is :", d)
                    if (edgeStyleSettings.enableGlow) {
                        return "url(#glow)";
                    } else {
                        return null;
                    }
                })
                // 应用箭头
                .attr("marker-end", d => {
                    console.log("d is :", d)
                    if (edgeStyleSettings.showArrow) {
                        switch (edgeStyleSettings.arrowStyle) {
                            case 'circle':
                                return "url(#circle-marker)";
                            case 'diamond':
                                return "url(#diamond-marker)";
                            case 'hollow':
                                return "url(#hollow-arrow)";
                            case 'default':
                                return "url(#arrow)";
                            default:
                                return "url(#arrow)";
                        }
                    } else {
                        return null;
                    }
                })

            // 应用颜色方案
            selection.attr("stroke", d => {
                // 检查是否为选中的边
                if (selectedEdge && d.id === selectedEdge.id) {
                    return "#ff6b35" // 保持选中边的高亮颜色
                }

                // 根据颜色方案设置颜色
                switch (edgeStyleSettings.colorScheme) {
                    case 'gradient':
                        // 渐变色（这里使用固定颜色，实际应用中可以根据需要调整）
                        return getColorForGraphId(d.graphId)
                    case 'category':
                        // 分类色（这里使用固定颜色，实际应用中可以根据需要调整）
                        return getColorForGraphId(d.graphId)
                    case 'random':
                        // 随机色
                        return getRandomColor()
                    default:
                        // 默认颜色
                        return getColorForGraphId(d.graphId)
                }
            })
        }

        // 生成随机颜色
        const getRandomColor = () => {
            const letters = '0123456789ABCDEF'
            let color = '#'
            for (let i = 0; i < 6; i++) {
                color += letters[Math.floor(Math.random() * 16)]
            }
            return color
        }

        // 创建路径数据
        const createPathData = (source, target, style) => {
            if (!source || !target) return null

            const x1 = source.x, y1 = source.y
            const x2 = target.x, y2 = target.y

            switch (style) {
                case 'curve': {
                    // 贝塞尔曲线
                    const cx = (x1 + x2) / 2;
                    const cy = (y1 + y2) / 2;
                    return `M${x1},${y1} Q${cx},${cy - 30} ${x2},${y2}`;
                }
                case 'bezier': {
                    // 三次贝塞尔曲线
                    const cx1 = x1 + (x2 - x1) * 0.25;
                    const cy1 = y1 + (y2 - y1) * 0.25;
                    const cx2 = x1 + (x2 - x1) * 0.75;
                    const cy2 = y1 + (y2 - y1) * 0.75;
                    return `M${x1},${y1} C${cx1},${cy1 - 20} ${cx2},${cy2 - 20} ${x2},${y2}`
                }
                case 'orthogonal': {
                    // 正交线
                    const midX = (x1 + x2) / 2
                    return `M${x1},${y1} L${midX},${y1} L${midX},${y2} L${x2},${y2}`
                }
                case 'dashed':
                    // 虚线（与直线相同，样式通过stroke-dasharray控制）
                    return `M${x1},${y1} L${x2},${y2}`
                default:
                    // 直线
                    return `M${x1},${y1} L${x2},${y2}`
            }
        }

        // 加载图数据
        const loadGraphData = () => {
            if (!svg) {
                console.warn('SVG元素不存在，无法加载数据')
                return
            }

            // 确保 zoomContainer 已经被定义
            if (!zoomContainer) {
                console.warn('zoomContainer 未定义，无法加载数据')
                return
            }

            const data = store.graphData
            console.log('加载图数据:', data)

            try {
                // 清除之前的所有元素，防止残留
                if (linkGroup) {
                    linkGroup.selectAll("*").remove()
                }
                if (nodeGroup) {
                    nodeGroup.selectAll("*").remove()
                }
                if (textGroup) {
                    textGroup.selectAll("*").remove()
                }

                // 如果是树形布局，先应用树形布局算法
                if (layoutType === 'tree' && typeof applyTreeLayout === 'function') {
                    applyTreeLayout()
                }

                // 更新力导向图数据
                simulation.nodes(data.nodes)

                // 处理链接数据，确保source和target指向节点对象而不是ID
                const links = data.edges.map(edge => {
                    const sourceNode = data.nodes.find(node => node.id === edge.source)
                    const targetNode = data.nodes.find(node => node.id === edge.target)
                    return {
                        ...edge,
                        source: sourceNode || edge.source,
                        target: targetNode || edge.target
                    }
                })

                simulation.force("link").links(links)

                // 重新创建连线元素
                const linkSelection = linkGroup.selectAll("path")
                    .data(links, d => d.id || `${d.source}-${d.target}`)

                // 添加新连线
                const linkEnter = linkSelection.enter()
                    .append("path")
                    .attr("class", "link")
                    .attr("stroke", d => getColorForGraphId(d.graphId))
                    .attr("stroke-opacity", 0.6)
                    .attr("stroke-width", edgeStyleSettings.width)
                    .attr("fill", "none")
                    .on("click", handleEdgeClick)
                    .on("dblclick", handleEdgeDblClick)

                // 更新连线
                linkElements = linkEnter.merge(linkSelection)
                    .attr("stroke", d => {
                        // 检查是否为选中的边
                        if (selectedEdge && d.id === selectedEdge.id) {
                            return "#ff6b35"; // 保持选中边的高亮颜色
                        }
                        // 根据graphId设置颜色
                        return getColorForGraphId(d.graphId);
                    })
                    .attr("stroke-width", d => {
                        // 检查是否为选中的边
                        if (selectedEdge && d.id === selectedEdge.id) {
                            return edgeStyleSettings.width + 1; // 保持选中边的高亮宽度
                        }
                        return d.style?.lineWidth || edgeStyleSettings.width; // 使用设置的宽度
                    })
                    .attr("stroke-opacity", d => d.style?.opacity || 0.6)
                    // 应用虚线样式
                    .attr("stroke-dasharray", d => {
                        console.log("d is :", d)
                        if (edgeStyleSettings.style === 'dashed') {
                            return "5,5";
                        } else {
                            return null;
                        }
                    })
                    // 应用发光效果
                    .attr("filter", d => {
                        console.log("d is :", d)
                        if (edgeStyleSettings.enableGlow) {
                            return "url(#glow)";
                        } else {
                            return null;
                        }
                    })
                    // 应用箭头
                    .attr("marker-end", d => {
                        console.log("d is :", d)
                        if (edgeStyleSettings.showArrow) {
                            switch (edgeStyleSettings.arrowStyle) {
                                case 'circle':
                                    return "url(#circle-marker)";
                                case 'diamond':
                                    return "url(#diamond-marker)";
                                case 'hollow':
                                    return "url(#hollow-arrow)";
                                case 'default':
                                    return "url(#arrow)";
                                default:
                                    return "url(#arrow)";
                            }
                        } else {
                            return null;
                        }
                    })
                    .attr("d", d => {
                        // 获取源节点和目标节点
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source);
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target);

                        // 创建路径数据
                        return createPathData(sourceNode, targetNode, edgeStyleSettings.style);
                    })

                // 移除旧连线
                linkSelection.exit().remove()

                // 创建连线标签元素
                let linkLabelElements = null;
                if (zoomContainer) {
                    const linkLabelGroup = zoomContainer.select(".link-labels")
                    if (linkLabelGroup.empty()) {
                        zoomContainer.append("g").attr("class", "link-labels")
                    }
                    linkLabelElements = zoomContainer.select(".link-labels").selectAll("text")
                        .data(links, d => d.id || `${d.source}-${d.target}`)
                } else {
                    console.warn('zoomContainer 未定义，无法创建连线标签元素')
                }

                // 添加新的连线标签
                const linkLabelEnter = linkLabelElements.enter()
                    .append("text")
                    .attr("class", "link-label")
                    .attr("text-anchor", "middle")
                    .attr("dy", -5)
                    .attr("font-size", "10px")
                    .attr("fill", "#666")
                    .attr("pointer-events", "none")

                // 更新连线标签
                linkLabelEnter.merge(linkLabelElements)
                    .text(d => d.label?.formatter || d.label || "")
                    .attr("x", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                case 'bezier':
                                    // 对于曲线，标签位置稍微偏移
                                    return (sourceNode.x + targetNode.x) / 2;
                                case 'orthogonal':
                                    // 对于正交线，标签放在中间点
                                    return (sourceNode.x + targetNode.x) / 2;
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.x + targetNode.x) / 2;
                            }
                        }
                        return 0;
                    })
                    .attr("y", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                    // 贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 15;
                                case 'bezier':
                                    // 三次贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 20;
                                case 'orthogonal':
                                    // 正交线，标签放在中间点
                                    return (sourceNode.y + targetNode.y) / 2;
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.y + targetNode.y) / 2;
                            }
                        }
                        return 0;
                    })

                // 移除旧连线标签
                linkLabelElements.exit().remove()

                // 更新连线标签位置
                linkLabelElements
                    .attr("x", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                case 'bezier':
                                    // 对于曲线，标签位置稍微偏移
                                    return (sourceNode.x + targetNode.x) / 2;
                                case 'orthogonal':
                                    // 对于正交线，标签放在中间点
                                    return (sourceNode.x + targetNode.x) / 2;
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.x + targetNode.x) / 2;
                            }
                        }
                        return 0;
                    })
                    .attr("y", d => {
                        const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)
                        if (sourceNode && targetNode) {
                            // 根据线条样式调整标签位置
                            switch (edgeStyleSettings.style) {
                                case 'curve':
                                    // 贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 15;
                                case 'bezier':
                                    // 三次贝塞尔曲线，标签位置稍微向上偏移
                                    return (sourceNode.y + targetNode.y) / 2 - 20;
                                case 'orthogonal':
                                    // 正交线，标签放在中间点
                                    return (sourceNode.y + targetNode.y) / 2;
                                default:
                                    // 直线，标签放在中间
                                    return (sourceNode.y + targetNode.y) / 2;
                            }
                        }
                        return 0;
                    })

                // 移除旧的连线标签
                linkLabelElements.exit().remove()

                // 清除所有现有节点
                // nodeGroup.selectAll("g").remove();

                // 绑定节点数据
                const nodeSelection = nodeGroup.selectAll("g")
                    .data(data.nodes, d => d.id)

                // 处理节点 enter、update 和 exit
                const nodeEnter = nodeSelection.enter()
                    .append("g")
                    .attr("class", "node-group")
                    .on("click", handleNodeClick)
                    .on("dblclick", handleNodeDblClick)
                    .on("contextmenu", handleNodeRightClick)
                    // 手动实现拖拽功能，避免与click事件冲突
                    .on("mousedown", handleNodeMouseDown)

                // 为新节点组添加圆形
                nodeEnter.append("circle")
                    .attr("class", "node")
                    .attr("r", d => d.symbolSize * 10 || 10) // 改为 symbolSize
                    .attr("fill", d => d.imageUrl ? "white" : getColorForNode(d)) // 如果有图片则显示白色背景
                    .attr("stroke", "#fff")
                    .attr("stroke-width", 1.5)
                    // 添加鼠标事件
                    .on("click", handleNodeClick)
                    .on("mouseover", handleNodeMouseOver)
                    .on("mousemove", handleNodeMouseMove)
                    .on("mouseout", handleNodeMouseOut)

                // 为新节点组添加图片（如果有的话）
                nodeEnter.append("image")
                    .attr("class", "node-image")
                    .attr("x", d => -(d.symbolSize * 10 || 10) * 0.7) // 改为 symbolSize
                    .attr("y", d => -(d.symbolSize * 10 || 10) * 0.7) // 改为 symbolSize
                    .attr("width", d => (d.symbolSize * 10 || 10) * 1.4) // 改为 symbolSize
                    .attr("height", d => (d.symbolSize * 10 || 10) * 1.4) // 改为 symbolSize
                    .attr("clip-path", "circle()") // 裁剪成圆形
                    .attr("display", d => d.imageUrl ? "block" : "none")
                    .attr("href", d => d.imageUrl || "")
                    // 添加鼠标事件
                    .on("click", handleNodeClick)
                    .on("mouseover", handleNodeMouseOver)
                    .on("mousemove", handleNodeMouseMove)
                    .on("mouseout", handleNodeMouseOut)

                // 合并 enter 和 update selection
                const nodeUpdate = nodeEnter.merge(nodeSelection)
                    .on("click", handleNodeClick) // 确保更新的节点也有点击事件
                    .on("dblclick", handleNodeDblClick)
                    .on("contextmenu", handleNodeRightClick)
                    // 手动实现拖拽功能，避免与click事件冲突
                    .on("mousedown", handleNodeMouseDown)

                // 更新圆形
                nodeUpdate.select("circle")
                    .attr("r", d => d.symbolSize * 10 || 10) // 改为 symbolSize
                    .attr("fill", d => d.imageUrl ? "white" : getColorForNode(d)) // 如果有图片则显示白色背景
                    .attr("stroke", d => {
                        // 检查是否为选中的节点
                        if (selectedNode && d.id === selectedNode.id) {
                            return "#ff6b35"; // 保持选中节点的高亮边框颜色
                        }
                        return "#fff"; // 默认边框颜色
                    })
                    .attr("stroke-width", d => {
                        // 检查是否为选中的节点
                        if (selectedNode && d.id === selectedNode.id) {
                            return 3; // 保持选中节点的高亮边框宽度
                        }
                        return 1.5; // 默认边框宽度
                    })
                    // 添加鼠标事件
                    .on("click", handleNodeClick)
                    .on("mouseover", handleNodeMouseOver)
                    .on("mousemove", handleNodeMouseMove)
                    .on("mouseout", handleNodeMouseOut)

                // 更新图片
                nodeUpdate.select("image")
                    .attr("x", d => -(d.symbolSize * 10 || 10) * 0.7) // 改为 symbolSize
                    .attr("y", d => -(d.symbolSize * 10 || 10) * 0.7) // 改为 symbolSize
                    .attr("width", d => (d.symbolSize * 10 || 10) * 1.4) // 改为 symbolSize
                    .attr("height", d => (d.symbolSize * 10 || 10) * 1.4) // 改为 symbolSize
                    .attr("display", d => d.imageUrl ? "block" : "none")
                    .attr("href", d => d.imageUrl || "")
                    // 添加鼠标事件
                    .on("click", handleNodeClick)
                    .on("mouseover", handleNodeMouseOver)
                    .on("mouseout", handleNodeMouseOut)

                // 更新节点元素
                nodeElements = nodeUpdate
                    .attr("transform", d => {
                        return `translate(${d.x}, ${d.y})`;
                    })

                // 移除旧节点组
                nodeSelection.exit().remove()

                // 绑定文本数据
                const textSelection = textGroup.selectAll("text")
                    .data(data.nodes, d => d.id)

                // 添加新文本
                const textEnter = textSelection.enter()
                    .append("text")
                    .attr("class", "node-label")
                    .attr("text-anchor", "middle")
                    .attr("dy", -15)
                    .text(d => d.name) // 改为 name
                    .on("click", handleNodeClick)
                    // 添加鼠标事件
                    .on("mouseover", handleNodeMouseOver)
                    .on("mousemove", handleNodeMouseMove)
                    .on("mouseout", handleNodeMouseOut)

                // 更新文本
                textElements = textEnter.merge(textSelection)
                    .text(d => d.name) // 改为 name
                    .attr("x", d => {
                        return d.x;
                    })
                    .attr("y", d => {
                        return d.y + 5;
                    })
                    .on("click", handleNodeClick)
                    // 添加鼠标事件
                    .on("mouseover", handleNodeMouseOver)
                    .on("mousemove", handleNodeMouseMove)
                    .on("mouseout", handleNodeMouseOut)

                // 移除旧文本
                textSelection.exit().remove()

                // 重启仿真（仅在力导向布局模式下）
                if (layoutType === 'force') {
                    simulation.alpha(1).restart();
                }

                console.log('图数据加载成功')

            } catch (error) {
                console.error('加载图数据失败:', error)
            }
        }

        // 获取节点颜色
        const getColorForNode = (node) => {
            if (node.style && node.style.fill) {
                return node.style.fill
            }
            // 使用分组ID作为颜色键
            const groupKey = node.groups ? node.groups[0] : 'default'
            // 使用d3-chromatic的颜色方案
            const colorIndex = Math.abs(groupKey.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0)) % 10
            return colorScale[colorIndex]
        }

        // 节点点击事件
        const handleNodeClick = (event, d) => {
            console.log('节点点击:', d)
            event.stopPropagation()

            // 清除右键拖动轨迹
            clearDragPath()

            // 清除之前的选择状态
            clearSelection()

            // 设置选中状态
            selectedNode = d
            // 直接修改节点圆形的属性，而不是通过event.currentTarget
            // 确保选择的是正确的节点组元素
            if (nodeElements) {
                nodeElements
                    .filter(node => node.id === d.id)
                    .select("circle")
                    .attr("stroke", "#ff6b35")
                    .attr("stroke-width", 3)
            }

            // 高亮相关节点
            highlightRelatedNodes(d.id)
            //向父组件传递点击事件
            emit('node-click', d)

            //实现拓展数据效果
            store.findGraphByNode(d.name || d.label, 5)
        }

        // 添加节点鼠标进入事件
        const handleNodeMouseOver = (event, d) => {
            // 创建或更新 tooltip
            let tooltip = d3.select(".node-tooltip")
            if (tooltip.empty()) {
                tooltip = d3.select("body")
                    .append("div")
                    .attr("class", "node-tooltip")
                    .style("position", "absolute")
                    .style("padding", "8px")
                    .style("background", "rgba(0, 0, 0, 0.8)")
                    .style("color", "white")
                    .style("border-radius", "4px")
                    .style("pointer-events", "none")
                    .style("font-size", "12px")
                    .style("z-index", "1000")
                    .style("max-width", "300px")
                    .style("word-wrap", "break-word")
            }

            // 显示 tooltip 并设置内容
            tooltip
                .style("display", "block")
                .html(d.desc || "暂无描述")
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 20) + "px")
        }

        // 添加节点鼠标移动事件
        const handleNodeMouseMove = (event) => {
            // console.log('节点鼠标移动:', event, d)
            const tooltip = d3.select(".node-tooltip")
            if (!tooltip.empty()) {
                tooltip
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY - 20) + "px")
            }
        }

        // 添加节点鼠标离开事件
        const handleNodeMouseOut = (event, d) => {
            console.log('节点鼠标离开:', event, d)
            const tooltip = d3.select(".node-tooltip")
            if (!tooltip.empty()) {
                tooltip.style("display", "none")
            }
        }

        // 节点双击事件
        const handleNodeDblClick = (event, d) => {
            event.stopPropagation()
            const newLabel = prompt('请输入新的节点标签:', d.label || '')
            if (newLabel !== null && newLabel !== d.label) {
                emit('node-edit', d.id, newLabel)
            }
        }

        // 添加分组事件处理函数
        const handleAddGroup = () => {
            const groupName = prompt('请输入新分组的名称:')
            if (groupName) {
                // 生成随机颜色
                const randomColor = customColorScheme[Math.floor(Math.random() * customColorScheme.length)]

                // 创建新分组对象
                const newGroup = {
                    id: `group_${Date.now()}`,
                    name: groupName,
                    color: randomColor
                }

                // 添加到store
                store.addGroup(newGroup)

                // 重新初始化分组显示
                initGroups()
            }
        }

        // 节点鼠标按下事件
        const handleNodeMouseDown = (event, d) => {
            // 只处理左键点击
            if (event.button !== 0) return;

            // 阻止事件冒泡
            event.stopPropagation();

            // 设置拖拽状态
            d.isDragging = false;
            const startX = event.clientX;
            const startY = event.clientY;

            // 保存节点初始位置
            const initialX = d.x;
            const initialY = d.y;

            // 添加鼠标移动和松开事件监听器
            const handleMouseMove = (moveEvent) => {
                // 阻止默认行为和事件冒泡
                moveEvent.preventDefault();
                moveEvent.stopPropagation();

                // 计算移动距离
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;

                // 如果移动距离超过阈值，开始拖拽
                if (!d.isDragging && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
                    d.isDragging = true;
                    // 调用拖拽开始处理函数
                    handleDragStart(event, d);
                }

                // 如果正在拖拽，更新位置
                if (d.isDragging) {
                    // 更新节点位置
                    d.x = initialX + dx;
                    d.y = initialY + dy;

                    // 调用拖拽处理函数
                    handleDrag(moveEvent, d);
                }
            };

            const handleMouseUp = (upEvent) => {
                // 阻止事件冒泡
                upEvent.stopPropagation();

                // 移除事件监听器
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);

                // 如果正在拖拽，调用拖拽结束处理函数
                if (d.isDragging) {
                    handleDragEnd(upEvent, d);
                }

                // 清除拖拽状态
                delete d.isDragging;
            };

            // 添加全局事件监听器
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            // 阻止默认行为，防止文本选择等
            event.preventDefault();
        }

        // 添加右键菜单相关的响应式数据
        const contextMenu = ref({
            visible: false,
            x: 0,
            y: 0
        });

        // 添加子菜单显示状态
        const showGroupSubMenu = ref(false);
        const showTagSubMenu = ref(false); // 添加标签子菜单显示状态

        // 用于存储右键点击的节点
        const selectedNodeForContextMenu = ref(null);

        // 修改节点右键点击事件
        const handleNodeRightClick = (event, d) => {
            console.log('节点右键点击:', d)
            event.preventDefault() // 阻止默认右键菜单
            event.stopPropagation()

            // 清除右键拖动轨迹
            clearDragPath()

            //如果没有已选中的节点，则生成右击菜单
            if (!selectedNode || selectedNode.id === d.id) {
                // 存储右键点击的节点
                selectedNodeForContextMenu.value = d;
                console.log('右键点击的节点:', selectedNodeForContextMenu.value);
                // 设置右键菜单位置
                contextMenu.value.x = event.clientX;
                contextMenu.value.y = event.clientY;
                contextMenu.value.visible = true;

                // 添加全局点击事件监听器，用于隐藏右键菜单
                const hideContextMenu = () => {
                    contextMenu.value.visible = false;
                    document.removeEventListener('click', hideContextMenu);
                };
                document.addEventListener('click', hideContextMenu);
                return;
            }

            emit('add-relationship', {
                source: selectedNode,
                target: d
            })

        }

        // 设为源节点
        const setAsSourceNode = () => {
            if (selectedNodeForContextMenu.value) {
                selectedNode = selectedNodeForContextMenu.value;
                // 高亮选中节点
                nodeElements
                    .filter(d => d.id === selectedNode.id)
                    .attr("stroke", "#ff6b35")
                    .attr("stroke-width", 3)
                contextMenu.value.visible = false;
                console.log('设为源节点:', selectedNodeForContextMenu.value);
            }
        }

        // 设为目标节点
        const setAsTargetNode = () => {
            if (selectedNode && selectedNodeForContextMenu.value && selectedNode.id !== selectedNodeForContextMenu.value.id) {
                // 触发添加关系事件，传递源节点和目标节点信息
                emit('add-relationship', {
                    source: selectedNode,
                    target: selectedNodeForContextMenu.value
                })
                contextMenu.value.visible = false;
                console.log('设为目标节点:', selectedNodeForContextMenu.value);
            }
        }

        // 添加标签到节点
        const addTagToNode = () => {
            if (selectedNodeForContextMenu.value) {
                const tag = prompt('请输入标签名称:');
                if (tag) {
                    // 这里可以添加具体的标签逻辑
                    console.log('为节点添加标签:', tag, selectedNodeForContextMenu.value);
                    // 触发添加标签事件
                    emit('add-tag', selectedNodeForContextMenu.value.id, tag);
                }
                contextMenu.value.visible = false;
            }
        }

        // 更新群组菜单状态
        const updateGroupMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                // 强制更新组件
                nextTick(() => {
                    // 重新计算群组状态
                })
            }
        }

        // 更新标签菜单状态
        const updateTagMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                // 强制更新组件
                nextTick(() => {
                    // 重新计算标签状态
                })
            }
        }

        // 处理标签选择
        const handleTagSelection = (tag) => {
            if (selectedNodeForContextMenu.value && tag) {
                console.log('选中的标签:', tag.name || tag.label);
                // 这里可以添加具体的标签处理逻辑
                // 例如：将标签应用到节点上
                contextMenu.value.visible = false;
            }
        }

        // 删除节点
        const deleteNode = () => {
            if (selectedNodeForContextMenu.value) {
                if (confirm(`确定要删除节点 "${selectedNodeForContextMenu.value.name}" 吗?`)) {
                    emit('delete-node', selectedNodeForContextMenu.value.id);
                    contextMenu.value.visible = false;
                }
            }
        }

        // 拓展节点
        const expandNode = () => {
            if (selectedNodeForContextMenu.value) {
                emit('expand-node', selectedNodeForContextMenu.value.id);
                contextMenu.value.visible = false;
            }
        }

        // 编辑节点
        const editNode = () => {
            if (selectedNodeForContextMenu.value) {
                const newLabel = prompt('请输入新的节点标签:', selectedNodeForContextMenu.value.label || selectedNodeForContextMenu.value.name || '')
                if (newLabel !== null && newLabel !== (selectedNodeForContextMenu.value.label || selectedNodeForContextMenu.value.name)) {
                    emit('node-edit', selectedNodeForContextMenu.value.id, newLabel)
                }
                contextMenu.value.visible = false;
            }
        }

        // 锁定/解锁节点
        const toggleNodeLock = () => {
            if (selectedNodeForContextMenu.value) {
                selectedNodeForContextMenu.value.locked = !selectedNodeForContextMenu.value.locked;
                emit('node-lock', selectedNodeForContextMenu.value.id, selectedNodeForContextMenu.value.locked);
                contextMenu.value.visible = false;
            }
        }

        // 设为标签
        const setAsTag = async () => {
            if (selectedNodeForContextMenu.value) {
                const nodeId = selectedNodeForContextMenu.value.id;

                // 准备数据
                const data = {
                    toNodeId: nodeId,
                    fromNodeId: nodeId, // addTag操作时fromNodeId和toNodeId相同
                    changeType: "addTag"
                };

                try {
                    // 调用store的changeGraph方法
                    const response = await store.changeGraph(data);

                    // 检查响应状态
                    if (response && response.status === 200) {
                        // 添加成功，创建新的标签对象
                        const newTag = {
                            id: nodeId,
                            name: selectedNodeForContextMenu.value.name || selectedNodeForContextMenu.value.label || `标签${nodeId}`
                        };

                        // 将新标签添加到store的tags中
                        store.tags.push(newTag);

                        // 触发标签显示更新
                        initTags();

                        console.log('标签添加成功:', newTag);
                    } else {
                        console.error('添加标签失败:', response?.message || '未知错误');
                    }
                } catch (error) {
                    console.error('添加标签时发生错误:', error);
                }

                contextMenu.value.visible = false;
            }
        }

        // 判断节点是否在指定group中
        const isNodeInGroup = (node, groupId) => {
            console.log("isNodeInGroup", node, groupId)
            console.log("selectedNodeForContextMenu", selectedNodeForContextMenu.value.groups)
            if (!selectedNodeForContextMenu.value || !selectedNodeForContextMenu.value.groups || !Array.isArray(selectedNodeForContextMenu.value.groups)) return false;
            return selectedNodeForContextMenu.value.groups.includes(groupId);
        }

        // 判断节点是否具有指定标签
        const isNodeInTag = (tagId) => {
            if (!selectedNodeForContextMenu.value || !selectedNodeForContextMenu.value.category) return false;

            // 检查节点的category属性是否包含该标签
            // category可能是数组或单个值
            if (Array.isArray(selectedNodeForContextMenu.value.category)) {
                return selectedNodeForContextMenu.value.category.includes(tagId);
            } else {
                return selectedNodeForContextMenu.value.category === tagId;
            }
        }

        // 切换节点group
        const toggleNodeGroup = (groupId) => {
            if (!selectedNodeForContextMenu.value) return;

            // 确保节点有groups属性
            if (!selectedNodeForContextMenu.value.groups) {
                selectedNodeForContextMenu.value.groups = [];
            }

            // 创建节点groups的副本
            const currentGroups = [...selectedNodeForContextMenu.value.groups];
            const groupIndex = currentGroups.indexOf(groupId);

            if (groupIndex > -1) {
                // 如果节点已在该group中，则移除
                currentGroups.splice(groupIndex, 1);
            } else {
                // 如果节点不在该group中，则添加
                currentGroups.push(groupId);
            }

            // 更新节点的groups
            selectedNodeForContextMenu.value.groups = currentGroups;

            // 更新store中的节点数据
            store.updateNode(selectedNodeForContextMenu.value.id, { groups: currentGroups });

            // 隐藏菜单
            contextMenu.value.visible = false;
        }

        // 边点击事件
        const handleEdgeClick = (event, d) => {
            event.stopPropagation()

            // 清除右键拖动轨迹
            clearDragPath()

            // 清除之前的选择状态
            clearSelection()

            // 设置选中状态
            selectedEdge = d
            // 直接修改连线的属性
            d3.select(event.currentTarget).attr("stroke", "#ff6b35").attr("stroke-width", 3)

            emit('edge-click', d)
        }

        // 边双击事件
        const handleEdgeDblClick = (event, d) => {
            event.stopPropagation()
            const newLabel = prompt('请输入新的连接标签:', d.label || '')
            if (newLabel !== null && newLabel !== d.label) {
                emit('edge-edit', d.id, newLabel)
            }
        }

        // 画布点击事件
        const handleCanvasClick = (event) => {
            console.log('画布点击:', event)
            if (event.target === svg.node()) {
                // 清除右键拖动轨迹
                clearDragPath()

                clearSelection()
                emit('canvas-click')
            }
        }

        // 画布右键按下事件
        const handleCanvasRightClick = (event) => {
            console.log('画布右键点击:', event)
            // console.log("isRightClickDragging before:", isRightClickDragging)
            event.preventDefault()
            event.stopPropagation()

            // 检查是否为双击（时间间隔小于300ms）
            const now = Date.now()
            if (lastRightClickTime && (now - lastRightClickTime < 300)) {
                // 双击右键，清除轨迹
                console.log("检测到右键双击，清除轨迹")
                clearDragPath()
                lastRightClickTime = 0 // 重置时间
                return
            }

            // 记录右键点击时间
            lastRightClickTime = now

            // 无论之前的状态如何，都先清除已存在的轨迹
            console.log("清除已存在的轨迹")
            clearDragPath()

            // 开始右键拖动
            isRightClickDragging = true
            // console.log("isRightClickDragging after set to true:", isRightClickDragging)
            dragPath = []

            // 记录起始点
            const point = d3.pointer(event, svg.select("g").node())
            dragPath.push({ x: point[0], y: point[1] })

            // 创建轨迹元素
            if (!dragPathElement) {
                dragPathElement = dragGroup.append("path")
                    .attr("class", "drag-path")
                    .attr("fill", "none")
                    .attr("stroke", "rgba(255, 215, 0, 0.8)")
                    .attr("stroke-width", 3)
                    .attr("stroke-linecap", "round")
                    .attr("stroke-linejoin", "round")
                    .attr("filter", "url(#glow)")
            }

            // 创建跟随光点
            if (!dragCircleElement) {
                dragCircleElement = dragGroup.append("circle")
                    .attr("class", "drag-circle")
                    .attr("r", 6)
                    .attr("fill", "gold")
                    .attr("filter", "url(#glow)")
            }

            // 更新光点位置
            dragCircleElement
                .attr("cx", point[0])
                .attr("cy", point[1])
                .attr("visibility", "visible")
        }

        // 画布鼠标移动事件
        const handleCanvasMouseMove = (event) => {
            // console.log('画布鼠标移动:', event)
            // console.log("isRightClickDragging in mousemove:", isRightClickDragging)
            if (isRightClickDragging) {
                event.preventDefault()
                const point = d3.pointer(event, svg.select("g").node())
                dragPath.push({ x: point[0], y: point[1] })

                // 保持路径点数在合理范围内
                if (dragPath.length > 50) {
                    dragPath.shift()
                }

                // 更新轨迹路径
                if (dragPathElement && dragPath.length > 1) {
                    const lineFunction = d3.line()
                        .x(d => d.x)
                        .y(d => d.y)
                        .curve(d3.curveCatmullRom.alpha(0.5))

                    dragPathElement
                        .attr("d", lineFunction(dragPath))
                        .attr("visibility", "visible")
                }

                // 更新光点位置
                if (dragCircleElement) {
                    dragCircleElement
                        .attr("cx", point[0])
                        .attr("cy", point[1])
                }

                // 检查是否与关系线相交
                checkPathIntersection(point)
            }
        }

        // 画布鼠标松开事件
        const handleCanvasMouseUp = () => {
            console.log("handleCanvasMouseUp called, isRightClickDragging:", isRightClickDragging)
            // 不再在鼠标松开时清除轨迹，保持轨迹显示直到用户右键双击或开始新的拖动
            if (isRightClickDragging) {
                // 可以在这里添加其他逻辑，但不清理轨迹
                console.log("右键拖动结束，保持轨迹显示")
            }
        }

        // 检查轨迹是否与关系线相交
        const checkPathIntersection = () => {
            // console.log("checkPathIntersection", currentPoint)
            if (!linkElements || dragPath.length < 2) return

            // 获取当前轨迹的最后一点和倒数第二点
            const lastPoint = dragPath[dragPath.length - 1]
            const prevPoint = dragPath[dragPath.length - 2]

            if (!prevPoint) return

            // 检查每条连线是否与轨迹相交
            linkElements.each(function (d) {
                const link = d3.select(this)
                const source = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source)
                const target = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target)

                if (!source || !target) return

                // 检查连线是否与轨迹线段相交
                if (isLineIntersecting(
                    { x: prevPoint.x, y: prevPoint.y },
                    { x: lastPoint.x, y: lastPoint.y },
                    { x: source.x, y: source.y },
                    { x: target.x, y: target.y }
                )) {
                    // 高亮相交的连线
                    link.attr("stroke", "#ff4757").attr("stroke-width", 4)
                    // 取消右键拖动效果
                    clearDragPath()
                    lastRightClickTime = 0 // 重置时间
                    // 显示删除确认
                    if (confirm(`确定要删除关系 "${d.label || d.id}" 吗?`)) {
                        emit('delete-relationship', d.id)
                    }

                    // 恢复连线样式
                    setTimeout(() => {
                        link.attr("stroke", d.style?.stroke || "#999")
                            .attr("stroke-width", d.style?.lineWidth || edgeStyleSettings.width)
                            .attr("d", () => {
                                // 获取源节点和目标节点
                                const sourceNode = typeof d.source === 'object' ? d.source : store.graphData.nodes.find(n => n.id === d.source);
                                const targetNode = typeof d.target === 'object' ? d.target : store.graphData.nodes.find(n => n.id === d.target);

                                // 创建路径数据
                                return createPathData(sourceNode, targetNode, edgeStyleSettings.style);
                            })
                    }, 300)
                }
            })
        }

        // 判断两条线段是否相交的辅助函数
        const isLineIntersecting = (p1, p2, p3, p4) => {
            // 快速边界检查
            const rect1 = {
                minX: Math.min(p1.x, p2.x),
                maxX: Math.max(p1.x, p2.x),
                minY: Math.min(p1.y, p2.y),
                maxY: Math.max(p1.y, p2.y)
            }

            const rect2 = {
                minX: Math.min(p3.x, p4.x),
                maxX: Math.max(p3.x, p4.x),
                minY: Math.min(p3.y, p4.y),
                maxY: Math.max(p3.y, p4.y)
            }

            if (rect1.maxX < rect2.minX || rect2.maxX < rect1.minX ||
                rect1.maxY < rect2.minY || rect2.maxY < rect1.minY) {
                return false
            }

            // 向量叉积函数
            const cross = (a, b, c) => {
                return (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y)
            }

            // 检查点是否在线段的同一侧
            const isSameSide = (a, b, c, d) => {
                const cp1 = cross(a, b, c)
                const cp2 = cross(a, b, d)
                return (cp1 * cp2 >= 0)
            }

            // 两条线段相交当且仅当它们不满足不相交的条件
            return !(isSameSide(p1, p2, p3, p4) || isSameSide(p3, p4, p1, p2))
        }

        // 拖拽开始事件
        // 拖拽开始事件
        const handleDragStart = (event, d) => {
            if (!d.locked) {
                isDragging = true
                // 只有在力导向布局模式下才启动仿真
                if (layoutType === 'force' && !event.active) {
                    simulation.alphaTarget(0.3).restart()
                }
            }
        }

        // 拖拽事件
        const handleDrag = (event, d) => {
            console.log("handleDrag")
            if (!d.locked) {
                // 更新节点组位置
                if (nodeElements) {
                    nodeElements
                        .filter(node => node.id === d.id)
                        .attr("transform", `translate(${d.x}, ${d.y})`)
                }

                // 更新文本位置
                if (textElements) {
                    textElements
                        .filter(node => node.id === d.id)
                        .attr("x", d.x)
                        .attr("y", d.y + 5)
                }

                // 更新相关的连线位置
                if (linkElements) {
                    linkElements
                        .filter(link => link.source.id === d.id || link.target.id === d.id)
                        .attr("d", link => {
                            // 获取源节点和目标节点
                            let sourceNode, targetNode;

                            if (link.source.id === d.id) {
                                sourceNode = { x: d.x, y: d.y };
                            } else {
                                sourceNode = typeof link.source === 'object' ? link.source : store.graphData.nodes.find(n => n.id === link.source);
                            }

                            if (link.target.id === d.id) {
                                targetNode = { x: d.x, y: d.y };
                            } else {
                                targetNode = typeof link.target === 'object' ? link.target : store.graphData.nodes.find(n => n.id === link.target);
                            }

                            // 创建路径数据
                            return createPathData(sourceNode, targetNode, edgeStyleSettings.style);
                        })
                }
            }
        }

        // 拖拽结束事件
        const handleDragEnd = (event, d) => {
            console.log("handleDragEnd")
            if (!d.locked) {
                isDragging = false
                // 只有在力导向布局模式下才停止仿真
                if (layoutType === 'force' && !event.active) {
                    simulation.alphaTarget(0)
                }
                // 发送节点位置更新事件
                emit('node-drag', d.id, { x: d.x, y: d.y })

                // 确保节点位置更新到store
                store.updateNode(d.id, { x: d.x, y: d.y });
            }
        }

        // 清除选择
        const clearSelection = () => {
            selectedNode = null
            selectedEdge = null
            // 不再直接修改元素的视觉样式，因为loadGraphData会处理样式更新
            // 选中状态的清除会在下次loadGraphData时通过更新逻辑自然实现
        }

        // 清除拖动轨迹
        const clearDragPath = () => {
            // console.log("clearDragPath called, isRightClickDragging before:", isRightClickDragging)
            if (dragPathElement) {
                dragPathElement.attr("visibility", "hidden")
                dragPathElement.attr("d", "") // 清空路径数据
            }
            if (dragCircleElement) {
                dragCircleElement.attr("visibility", "hidden")
            }
            dragPath = []
            isRightClickDragging = false
            // console.log("clearDragPath finished, isRightClickDragging after:", isRightClickDragging)
        }

        // 高亮相关节点
        const highlightRelatedNodes = (nodeId) => {
            // 清除之前的高亮
            clearHighlight()

            // 获取相关数据
            const relatedData = store.selectedNodeRelatedData

            // 高亮选中节点
            if (nodeElements) {
                nodeElements
                    .filter(d => d.id === nodeId)
                    .attr("stroke", "#ff6b35")
                    .attr("stroke-width", 3)
            }

            // 高亮相关节点
            if (nodeElements) {
                nodeElements
                    .filter(d => d.id !== nodeId && relatedData.nodes.some(n => n.id === d.id))
                    .attr("stroke", "#61DDAA")
                    .attr("stroke-width", 2)
            }

            // 高亮相关连线
            if (linkElements) {
                linkElements
                    .filter(d => relatedData.edges.some(e => e.id === d.id))
                    .attr("stroke", "#61DDAA")
                    .attr("stroke-width", edgeStyleSettings.width)
            }
        }

        // 刷新图形
        const refreshGraph = () => {
            nextTick(() => {
                loadGraphData()
            })
        }

        // 高亮匹配节点（根据搜索文本）
        const highlightMatchingNodes = (searchText) => {
            // 检查搜索文本是否为空或只包含空格
            if (!searchText || searchText.trim() === '') {
                clearHighlight()
                return
            }

            const lowerSearchText = searchText.toLowerCase()

            // 高亮匹配的节点
            if (nodeElements) {
                nodeElements
                    .attr("stroke", d => {
                        // 检查节点名称或描述是否匹配搜索文本
                        const nameMatch = d.name && d.name.toLowerCase().includes(lowerSearchText)
                        const descMatch = d.desc && d.desc.toLowerCase().includes(lowerSearchText)

                        if (nameMatch || descMatch) {
                            return "#ff6b35" // 匹配节点使用橙色边框
                        }

                        // 检查是否为选中的节点
                        if (selectedNode && d.id === selectedNode.id) {
                            return "#ff6b35" // 保持选中节点的高亮边框颜色
                        }

                        return "#fff" // 默认边框颜色
                    })
                    .attr("stroke-width", d => {
                        // 检查节点名称或描述是否匹配搜索文本
                        const nameMatch = d.name && d.name.toLowerCase().includes(lowerSearchText)
                        const descMatch = d.desc && d.desc.toLowerCase().includes(lowerSearchText)

                        if (nameMatch || descMatch) {
                            return 3 // 匹配节点使用更宽的边框
                        }

                        // 检查是否为选中的节点
                        if (selectedNode && d.id === selectedNode.id) {
                            return 3 // 保持选中节点的高亮边框宽度
                        }

                        return 1.5 // 默认边框宽度
                    })
                    .attr("opacity", d => {
                        // 检查节点名称或描述是否匹配搜索文本
                        const nameMatch = d.name && d.name.toLowerCase().includes(lowerSearchText)
                        const descMatch = d.desc && d.desc.toLowerCase().includes(lowerSearchText)

                        // 如果有搜索文本且节点不匹配，则降低透明度
                        if (searchText && !nameMatch && !descMatch) {
                            return 0.3 // 降低不匹配节点的透明度
                        }

                        return 1 // 匹配节点或无搜索文本时保持完全不透明
                    })
            }

            // 调整连线的透明度
            if (linkElements) {
                linkElements
                    .attr("stroke-opacity", d => {
                        // 获取连线的源节点和目标节点
                        const sourceNode = typeof d.source === 'object' ? d.source : store.nodes.find(node => node.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.nodes.find(node => node.id === d.target)

                        // 检查源节点和目标节点是否匹配搜索文本
                        const sourceNameMatch = sourceNode && sourceNode.name && sourceNode.name.toLowerCase().includes(lowerSearchText)
                        const sourceDescMatch = sourceNode && sourceNode.desc && sourceNode.desc.toLowerCase().includes(lowerSearchText)
                        const targetNameMatch = targetNode && targetNode.name && targetNode.name.toLowerCase().includes(lowerSearchText)
                        const targetDescMatch = targetNode && targetNode.desc && targetNode.desc.toLowerCase().includes(lowerSearchText)

                        // 如果有搜索文本且连线的两端节点都不匹配，则降低透明度
                        if (searchText && !sourceNameMatch && !sourceDescMatch && !targetNameMatch && !targetDescMatch) {
                            return 0.1 // 降低不匹配连线的透明度
                        }

                        // 检查是否为选中的边
                        if (selectedEdge && d.id === selectedEdge.id) {
                            return 1 // 保持选中边的高亮透明度
                        }

                        return 0.6 // 默认透明度
                    })
                    .attr("stroke", d => {
                        // 获取连线的源节点和目标节点
                        const sourceNode = typeof d.source === 'object' ? d.source : store.nodes.find(node => node.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.nodes.find(node => node.id === d.target)

                        // 检查源节点和目标节点是否匹配搜索文本
                        const sourceNameMatch = sourceNode && sourceNode.name && sourceNode.name.toLowerCase().includes(lowerSearchText)
                        const sourceDescMatch = sourceNode && sourceNode.desc && sourceNode.desc.toLowerCase().includes(lowerSearchText)
                        const targetNameMatch = targetNode && targetNode.name && targetNode.name.toLowerCase().includes(lowerSearchText)
                        const targetDescMatch = targetNode && targetNode.desc && targetNode.desc.toLowerCase().includes(lowerSearchText)

                        // 如果连线的两端节点有任何一个匹配，则高亮连线
                        if (sourceNameMatch || sourceDescMatch || targetNameMatch || targetDescMatch) {
                            return "#ff6b35" // 匹配连线使用橙色
                        }

                        // 检查是否为选中的边
                        if (selectedEdge && d.id === selectedEdge.id) {
                            return "#ff6b35" // 保持选中边的高亮颜色
                        }

                        return d.style?.stroke || "#999" // 默认颜色
                    })
                    .attr("stroke-width", d => {
                        // 获取连线的源节点和目标节点
                        const sourceNode = typeof d.source === 'object' ? d.source : store.nodes.find(node => node.id === d.source)
                        const targetNode = typeof d.target === 'object' ? d.target : store.nodes.find(node => node.id === d.target)

                        // 检查源节点和目标节点是否匹配搜索文本
                        const sourceNameMatch = sourceNode && sourceNode.name && sourceNode.name.toLowerCase().includes(lowerSearchText)
                        const sourceDescMatch = sourceNode && sourceNode.desc && sourceNode.desc.toLowerCase().includes(lowerSearchText)
                        const targetNameMatch = targetNode && targetNode.name && targetNode.name.toLowerCase().includes(lowerSearchText)
                        const targetDescMatch = targetNode && targetNode.desc && targetNode.desc.toLowerCase().includes(lowerSearchText)

                        // 如果连线的两端节点有任何一个匹配，则加粗连线
                        if (sourceNameMatch || sourceDescMatch || targetNameMatch || targetDescMatch) {
                            return edgeStyleSettings.width + 1 // 匹配连线使用更宽的线宽
                        }

                        // 检查是否为选中的边
                        if (selectedEdge && d.id === selectedEdge.id) {
                            return edgeStyleSettings.width + 1 // 保持选中边的高亮宽度
                        }

                        return d.style?.lineWidth || edgeStyleSettings.width // 使用设置的宽度
                    })
            }
        }

        // 清除高亮
        const clearHighlight = () => {
            try {
                // 恢复节点默认样式，但保留选中节点的高亮状态
                if (nodeElements) {
                    nodeElements
                        .attr("stroke", d => {
                            // 检查是否为选中的节点
                            if (selectedNode && d.id === selectedNode.id) {
                                return "#ff6b35" // 保持选中节点的高亮边框颜色
                            }
                            return "#fff" // 默认边框颜色
                        })
                        .attr("stroke-width", d => {
                            // 检查是否为选中的节点
                            if (selectedNode && d.id === selectedNode.id) {
                                return 3 // 保持选中节点的高亮边框宽度
                            }
                            return 1.5 // 默认边框宽度
                        })
                        .attr("opacity", 1) // 恢复节点透明度
                }

                // 恢复连线默认样式，但保留选中边的高亮状态
                if (linkElements) {
                    linkElements
                        .attr("stroke", d => {
                            // 检查是否为选中的边
                            if (selectedEdge && d.id === selectedEdge.id) {
                                return "#ff6b35" // 保持选中边的高亮颜色
                            }
                            return "#999" // 默认颜色
                        })
                        .attr("stroke-width", d => {
                            // 检查是否为选中的边
                            if (selectedEdge && d.id === selectedEdge.id) {
                                return edgeStyleSettings.width + 1 // 保持选中边的高亮宽度
                            }
                            return edgeStyleSettings.width // 使用设置的宽度
                        })
                        .attr("stroke-opacity", d => {
                            // 检查是否为选中的边
                            if (selectedEdge && d.id === selectedEdge.id) {
                                return 1 // 保持选中边的高亮透明度
                            }
                            return 0.6 // 默认透明度
                        })
                }
            } catch (error) {
                console.warn('清除高亮状态时出错:', error)
            }
        }

        // 重置布局
        const resetLayout = () => {
            if (simulation) {
                // 将所有节点位置重置到中心区域附近
                const nodes = store.graphData.nodes;
                const centerX = width / 2;
                const centerY = height / 2;
                const radius = Math.min(width, height) * 0.3;

                nodes.forEach((node, i) => {
                    // 将节点位置重置到中心区域范围内
                    const angle = (i / nodes.length) * 2 * Math.PI;
                    node.x = centerX + Math.cos(angle) * radius * Math.random();
                    node.y = centerY + Math.sin(angle) * radius * Math.random();

                });

                // 重新启动仿真（仅在力导向布局模式下）
                if (layoutType === 'force') {
                    simulation.nodes(nodes);
                    simulation.alpha(1).restart();
                } else {
                    // 在无布局模式下，直接刷新图形
                    refreshGraph();
                }
            }
        }

        // 适应视图
        const fitView = () => {
            if (!svg || !store.graphData.nodes.length || !zoomBehavior) return;

            // 计算所有节点的边界框
            const nodes = store.graphData.nodes;
            if (nodes.length === 0) return;

            let minX = Infinity, minY = Infinity;
            let maxX = -Infinity, maxY = -Infinity;

            nodes.forEach(node => {
                if (node.x < minX) minX = node.x;
                if (node.x > maxX) maxX = node.x;
                if (node.y < minY) minY = node.y;
                if (node.y > maxY) maxY = node.y;
            });

            // 添加边距
            const margin = 100;
            minX -= margin;
            minY -= margin;
            maxX += margin;
            maxY += margin;

            // 计算缩放比例
            const scaleX = width / (maxX - minX);
            const scaleY = height / (maxY - minY);
            const scale = Math.min(scaleX, scaleY, 1); // 不放大超过原始大小

            // 计算平移
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;
            const translateX = width / 2 - centerX * scale;
            const translateY = height / 2 - centerY * scale;

            // 使用D3的zoom行为应用变换
            svg.transition()
                .duration(500)
                .call(zoomBehavior.transform, d3.zoomIdentity
                    .translate(translateX, translateY)
                    .scale(scale)
                );
        }

        // 放大
        const zoomIn = () => {
            if (svg && zoomBehavior) {
                // 获取当前的变换
                const currentTransform = d3.zoomTransform(svg.node());

                // 计算新的缩放比例（增加20%）
                const newScale = Math.min(currentTransform.k * 1.2, 10); // 限制最大缩放

                // 应用新的变换
                svg.transition()
                    .duration(300)
                    .call(zoomBehavior.transform, d3.zoomIdentity
                        .translate(currentTransform.x, currentTransform.y)
                        .scale(newScale)
                    );
            }
        }

        // 缩小
        const zoomOut = () => {
            if (svg && zoomBehavior) {
                // 获取当前的变换
                const currentTransform = d3.zoomTransform(svg.node());

                // 计算新的缩放比例（减少20%）
                const newScale = Math.max(currentTransform.k * 0.8, 0.1); // 限制最小缩放

                // 应用新的变换
                svg.transition()
                    .duration(300)
                    .call(zoomBehavior.transform, d3.zoomIdentity
                        .translate(currentTransform.x, currentTransform.y)
                        .scale(newScale)
                    );
            }
        }

        // 全屏切换
        const toggleFullscreen = () => {
            const container = graphContainer.value
            if (!container) return

            if (!isFullscreen) {
                // 进入全屏
                if (container.requestFullscreen) {
                    container.requestFullscreen()
                } else if (container.mozRequestFullScreen) {
                    container.mozRequestFullScreen()
                } else if (container.webkitRequestFullscreen) {
                    container.webkitRequestFullscreen()
                } else if (container.msRequestFullscreen) {
                    container.msRequestFullscreen()
                }
                isFullscreen = true

                // 全屏时将文字颜色设置为白色
                setTimeout(() => {
                    if (tagsContainer.value) {
                        tagsContainer.value.classList.add('fullscreen-tags');
                        // 更新所有标签项的文字颜色为白色
                        const tagItems = tagsContainer.value.querySelectorAll('.tag-item');
                        tagItems.forEach(item => {
                            item.classList.add('fullscreen-white');
                        });
                    }
                    // 更新节点标签颜色为白色
                    if (textElements) {
                        textElements.attr("fill", "white");
                    }
                }, 100);
            } else {
                // 退出全屏
                if (document.exitFullscreen) {
                    document.exitFullscreen()
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen()
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen()
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen()
                }
                isFullscreen = false

                // 退出全屏时将文字颜色设置回黑色
                setTimeout(() => {
                    if (tagsContainer.value) {
                        tagsContainer.value.classList.remove('fullscreen-tags');
                        // 恢复所有标签项的文字颜色
                        const tagItems = tagsContainer.value.querySelectorAll('.tag-item');
                        tagItems.forEach(item => {
                            item.classList.remove('fullscreen-white');
                        });
                    }
                    // 恢复节点标签颜色为默认颜色
                    if (textElements) {
                        textElements.attr("fill", "#2d3748");
                    }
                }, 100);
            }

            // 调整图形大小
            setTimeout(() => {
                resizeGraph()
            }, 100)
        }

        // 调整图形大小
        const resizeGraph = () => {
            if (!graphContainer.value || !svg) return

            try {
                width = graphContainer.value.clientWidth || 800
                // 计算标签容器高度
                let tagsHeight = 0
                if (tagsContainer.value) {
                    tagsHeight = tagsContainer.value.offsetHeight || 50
                }

                height = (graphContainer.value.clientHeight - tagsHeight) || 600

                svg.attr("width", width).attr("height", height)

                // 更新中心力
                if (simulation) {
                    simulation.force("center", d3.forceCenter(width / 2, height / 2))
                    simulation.alpha(1).restart()
                }
            } catch (error) {
                console.warn('调整图形大小失败:', error)
            }
        }

        // 监听store数据版本变化
        watch(
            () => store.dataVersion,
            (newVersion, oldVersion) => {
                if (oldVersion !== undefined && newVersion > oldVersion) {
                    console.log('检测到数据版本变化，刷新图形')
                    nextTick(() => {
                        loadGraphData()
                    })
                }
            }
        )

        // 监听标签数据变化
        watch(
            () => store.tags,
            (newTags, oldTags) => {
                console.log('检测到标签数据变化，更新标签显示')
                console.log('newTags:', newTags)
                console.log('oldTags:', oldTags)
                nextTick(() => {
                    initTags()
                    // 确保标签颜色正确设置
                    setTimeout(() => {
                        ensureTagColors()
                    }, 50)
                })
            },
            { deep: true } // 添加深度监听
        )

        // 监听分组数据变化
        watch(
            () => store.groups,
            (newGroups, oldGroups) => {
                console.log('检测到分组数据变化，更新分组显示')
                console.log('newGroups:', newGroups)
                console.log('oldGroups:', oldGroups)
                nextTick(() => {
                    initGroups()
                    // 确保分组颜色正确设置
                    setTimeout(() => {
                        ensureGroupColors()
                    }, 50)
                })
            },
            { deep: true } // 添加深度监听
        )

        // 监听选中标签变化
        watch(
            () => store.selectedTags,
            (newSelectedTags, oldSelectedTags) => {
                console.log('检测到选中标签变化，更新标签显示')
                console.log('newSelectedTags:', newSelectedTags)
                console.log('oldSelectedTags:', oldSelectedTags)
                nextTick(() => {
                    updateTagsDisplay()
                    // 确保标签颜色正确设置
                    setTimeout(() => {
                        ensureTagColors()
                    }, 50)
                })
            }
        )

        // 监听选中分组变化
        watch(
            () => store.filters.groups,
            (newSelectedGroups, oldSelectedGroups) => {
                console.log('检测到选中分组变化，更新分组显示')
                console.log('newSelectedGroups:', newSelectedGroups)
                console.log('oldSelectedGroups:', oldSelectedGroups)
                nextTick(() => {
                    updateGroupsDisplay()
                    // 确保分组颜色正确设置
                    setTimeout(() => {
                        ensureGroupColors()
                    }, 50)
                })
            }
        )

        // 监听窗口大小变化
        const handleResize = () => {
            resizeGraph()
        }

        // 监听全屏变化
        const handleFullscreenChange = () => {
            isFullscreen = !!(document.fullscreenElement ||
                document.mozFullScreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement)

            // 根据全屏状态更新标签文字颜色
            if (tagsContainer.value) {
                if (isFullscreen) {
                    tagsContainer.value.classList.add('fullscreen-tags');
                    // 更新所有标签项的文字颜色为白色
                    const tagItems = tagsContainer.value.querySelectorAll('.tag-item');
                    tagItems.forEach(item => {
                        item.classList.add('fullscreen-white');
                    });
                    // 更新节点标签颜色为白色
                    if (textElements) {
                        textElements.attr("fill", "white");
                    }
                } else {
                    tagsContainer.value.classList.remove('fullscreen-tags');
                    // 恢复所有标签项的文字颜色
                    const tagItems = tagsContainer.value.querySelectorAll('.tag-item');
                    tagItems.forEach(item => {
                        item.classList.remove('fullscreen-white');
                    });
                    // 恢复节点标签颜色为默认颜色
                    if (textElements) {
                        textElements.attr("fill", "#2d3748");
                    }
                }
            }

            resizeGraph()
        }

        // 生命周期
        onMounted(() => {
            console.log('KnowledgeGraphCanvas组件挂载')
            nextTick(() => {
                initGraph()
                // 延迟初始化标签，确保store中的数据已加载
                setTimeout(() => {
                    console.log('延迟初始化标签显示')
                    console.log('store中的标签数据:', store.tags)
                    initTags()
                }, 100)
                // 添加画布事件监听器
                const svgElement = d3.select(graphContainer.value)
                svgElement.on("click", handleCanvasClick)
                svgElement.on("contextmenu", handleCanvasRightClick)
                svgElement.on("mousemove", handleCanvasMouseMove)
                // 只使用一个鼠标松开事件监听器
                svgElement.on("mouseup", handleCanvasMouseUp)
                // 不再使用mouseleave事件，避免重复触发

                // 添加全局鼠标松开事件监听器，确保在画布外松开鼠标也能处理
                document.addEventListener('mouseup', handleCanvasMouseUp)

                window.addEventListener('resize', handleResize)
                document.addEventListener('fullscreenchange', handleFullscreenChange)
                document.addEventListener('mozfullscreenchange', handleFullscreenChange)
                document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
                document.addEventListener('msfullscreenchange', handleFullscreenChange)

                // 添加滤镜定义
                if (svg) {
                    const defs = svg.append("defs")

                    // 添加发光滤镜
                    const filter = defs.append("filter")
                        .attr("id", "glow")
                        .attr("x", "-50%")
                        .attr("y", "-50%")
                        .attr("width", "200%")
                        .attr("height", "200%")

                    filter.append("feGaussianBlur")
                        .attr("stdDeviation", "3")
                        .attr("result", "coloredBlur")

                    const feMerge = filter.append("feMerge")
                    feMerge.append("feMergeNode")
                        .attr("in", "coloredBlur")
                    feMerge.append("feMergeNode")
                        .attr("in", "SourceGraphic")

                    // 添加箭头标记定义
                    // 添加默认箭头
                    const arrowMarker = defs.append("marker")  // eslint-disable-line no-unused-vars
                        .attr("id", "arrow")
                        .attr("viewBox", "0 -5 10 10")
                        .attr("refX", 15)
                        .attr("refY", 0)
                        .attr("markerWidth", 6)
                        .attr("markerHeight", 6)
                        .attr("orient", "auto")
                        .append("path")
                        .attr("d", "M0,-5L10,0L0,5")
                        .attr("fill", "#999")

                    // 添加空心三角箭头
                    const hollowArrowMarker = defs.append("marker")  // eslint-disable-line no-unused-vars
                        .attr("id", "hollow-arrow")
                        .attr("viewBox", "0 -5 10 10")
                        .attr("refX", 15)
                        .attr("refY", 0)
                        .attr("markerWidth", 6)
                        .attr("markerHeight", 6)
                        .attr("orient", "auto")
                        .append("path")
                        .attr("d", "M0,-5L10,0L0,5Z")
                        .attr("fill", "white")
                        .attr("stroke", "#999")
                        .attr("stroke-width", "1")

                    // 添加圆形箭头
                    const circleMarker = defs.append("marker")  // eslint-disable-line no-unused-vars
                        .attr("id", "circle-marker")
                        .attr("viewBox", "0 0 10 10")
                        .attr("refX", 15) // 调整refX以正确显示
                        .attr("refY", 5)
                        .attr("markerWidth", 6)
                        .attr("markerHeight", 6)
                        .attr("orient", "auto")
                        .append("circle")
                        .attr("cx", 5)
                        .attr("cy", 5)
                        .attr("r", 3)
                        .attr("fill", "#999")

                    // 添加菱形箭头
                    const diamondMarker = defs.append("marker")  // eslint-disable-line no-unused-vars
                        .attr("id", "diamond-marker")
                        .attr("viewBox", "0 0 10 10")
                        .attr("refX", 15) // 调整refX以正确显示
                        .attr("refY", 5)
                        .attr("markerWidth", 8)
                        .attr("markerHeight", 8)
                        .attr("orient", "auto")
                        .append("path")
                        .attr("d", "M5,0L10,5L5,10L0,5Z")
                        .attr("fill", "#999")
                }
            })
        })

        onUnmounted(() => {
            console.log('KnowledgeGraphCanvas组件卸载')

            // 清除右键拖动轨迹
            clearDragPath()

            if (simulation) {
                simulation.stop()
            }

            if (svg) {
                svg.remove()
            }

            // 移除全局事件监听器
            document.removeEventListener('mouseup', handleCanvasMouseUp)

            window.removeEventListener('resize', handleResize)
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
            document.removeEventListener('msfullscreenchange', handleFullscreenChange)
        })

        // 暴露方法给父组件
        return {
            graphContainer,
            tagsContainer,
            groupsContainer,  // 添加分组容器引用
            contextMenu,  // 添加这一行
            selectedNodeForContextMenu,  // 添加这一行
            showGroupSubMenu,  // 添加子菜单显示状态
            showTagSubMenu,  // 添加标签子菜单显示状态
            store,  // 添加store到返回值中
            refreshGraph,
            resetLayout,
            fitView,
            zoomIn,
            zoomOut,
            highlightRelatedNodes,
            toggleFullscreen,
            toggleTagSelection,
            toggleGroupSelection,  // 添加分组切换方法
            initTags,
            initGroups,  // 添加分组初始化方法
            updateTagsDisplay,
            updateGroupsDisplay,  // 添加分组更新显示方法
            ensureTagColors,
            ensureGroupColors,  // 添加分组颜色确保方法
            forceRefreshTags,
            forceRefreshGroups,  // 添加分组强制刷新方法
            testTagStyles,
            testGroupStyles,  // 添加分组样式测试方法
            highlightMatchingNodes,
            clearHighlight,
            clearDragPath, // 添加这一行
            setEdgeStyle, // 添加设置边样式方法
            applyEdgeStyle, // 添加应用边样式方法
            // 添加右键菜单相关的方法
            setAsSourceNode,
            setAsTargetNode,
            addTagToNode,
            deleteNode,
            expandNode,
            editNode,
            toggleNodeLock,
            setAsTag,
            isNodeInGroup,
            isNodeInTag,
            toggleNodeGroup,
            updateGroupMenu,
            updateTagMenu,  // 添加标签菜单更新方法
            handleTagSelection,  // 添加标签选择处理方法
            // 添加设置布局类型的方法
            setLayoutType: (type) => {
                layoutType = type
            },
            // 添加获取根节点的方法（target节点作为根节点）
            getRootNodes: () => {
                const nodes = store.graphData.nodes
                const edges = store.graphData.edges

                // 获取所有作为source的节点（有出边的节点）
                const sourceNodeIds = new Set(edges.map(edge =>
                    typeof edge.source === 'object' ? edge.source.id : edge.source
                ))

                // 根节点是没有出边的节点（即不在sourceNodeIds中的节点）
                return nodes.filter(node => !sourceNodeIds.has(node.id))
            },

        }
    }
}
</script>

<style scoped>
.knowledge-graph-canvas {
    width: 100%;
    height: 100%;
    position: relative;
    background-color: #ffffff;
    border: 1px solid #e1e5eb;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.tags-container {
    padding: 10px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e1e5eb;
    min-height: 20px;
    max-height: 100px;
    overflow-x: auto;
    overflow-y: hidden;
}

/* 分组容器样式 */
.groups-container {
    padding: 10px;
    background-color: #f0f0f0;
    border-bottom: 1px solid #e1e5eb;
    min-height: 20px;
    max-height: 100px;
    overflow-x: auto;
    overflow-y: hidden;
}

/* 全屏时的标签容器样式 */
.tags-container.fullscreen-tags {
    background-color: rgba(0, 0, 0, 0.8);
}

/* 全屏时的分组容器样式 */
.groups-container.fullscreen-groups {
    background-color: rgba(0, 0, 0, 0.8);
}

.tags-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.groups-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.graph-container {
    width: 100%;
    height: calc(100% - 50px);
    flex: 1;
    min-height: 0;
    /* 防止flex项目溢出 */
}

.knowledge-graph-svg {
    width: 100%;
    height: 100%;
    display: block;
}

.node {
    cursor: pointer;
    transition: r 0.2s ease;
}

.node:hover {
    r: calc(attr(r) + 2);
}

.link {
    cursor: pointer;
}

.link:hover {
    stroke-width: 3;
}

.link-label {
    font-family: Arial, sans-serif;
    font-size: 10px;
    fill: #666;
    pointer-events: none;
    user-select: none;
}

.node-image {
    pointer-events: none;
}

.node-label {
    font-family: Arial, sans-serif;
    font-size: 12px;
    fill: #2d3748;
    pointer-events: none;
    user-select: none;
}

/* 添加 tooltip 样式 */
.node-tooltip {
    position: absolute;
    padding: 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border-radius: 4px;
    pointer-events: none;
    font-size: 12px;
    z-index: 1000;
    max-width: 300px;
    word-wrap: break-word;
    display: none;
}

/* 右键菜单样式 */
.context-menu {
    position: fixed;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    min-width: 150px;
    padding: 4px 0;
}

.context-menu-item {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #333;
    position: relative;
}

.context-menu-item:hover {
    background-color: #f5f5f5;
}

.menu-icon {
    margin-right: 8px;
    font-size: 16px;
}

.context-submenu {
    position: absolute;
    left: 100%;
    top: 0;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    min-width: 120px;
    padding: 4px 0;
}

.submenu-trigger:hover .context-submenu {
    display: block;
}

.context-submenu .context-menu-item {
    padding: 8px 12px;
}

.context-submenu .context-menu-item:hover {
    background-color: #f5f5f5;
}

/* Group菜单项样式 */
.context-menu-item.group-in {
    color: #1890ff;
    font-weight: bold;
}

.context-menu-item.group-out {
    color: #999;
}

/* 右键拖动光效样式 */
.drag-path {
    stroke: gold;
    stroke-width: 3;
    fill: none;
    filter: url(#glow);
}

.drag-circle {
    fill: gold;
    filter: url(#glow);
}
</style>
<!-- 为动态创建的标签元素使用非scoped样式 -->
<style>
.tag-item {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    border: 2px solid transparent;
    user-select: none;
    /* 确保背景色可以被JavaScript设置覆盖 */
    background-color: transparent !important;
}

.tag-item.selected {
    border-color: #cab644;
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 1;
    /* 不设置背景色，让JavaScript控制 */
}

.tag-item.unselected {
    border-color: gray;
    opacity: 0.3;
    color: white;
    /* 不设置背景色，让JavaScript控制 */
}

/* 全屏时的标签项样式 */
.tag-item.fullscreen-white {
    color: white !important;
}

.tag-item:hover {
    opacity: 1;
    transform: scale(1.05);
}

/* 分组项样式 */
.group-item {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    border: 2px solid transparent;
    user-select: none;
    /* 确保背景色可以被JavaScript设置覆盖 */
    background-color: transparent !important;
}

.group-item.selected {
    border-color: #cab644;
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 1;
    /* 不设置背景色，让JavaScript控制 */
}

.group-item.unselected {
    border-color: gray;
    opacity: 0.3;
    color: white;
    /* 不设置背景色，让JavaScript控制 */
}

/* 全屏时的分组项样式 */
.group-item.fullscreen-white {
    color: white !important;
}

.group-item:hover {
    opacity: 1;
    transform: scale(1.05);
}

/* 添加分组按钮样式 */
.add-group-button {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #333;
    border: 2px solid #ccc;
    user-select: none;
    background-color: #f0f0f0 !important;
    margin-left: 5px;
}

.add-group-button:hover {
    background-color: #e0e0e0 !important;
    transform: scale(1.1);
}
</style>
